import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  getChapterInfo,
  getChapterList,
  getQuranPage,
  searchQuran,
  getVersePageNumber,
  getVerseByKey,
  getRecitationResources,
  getVerseTranslation,
  getVerseRecitation,
  getVerseTafseer,
  getTafsirAvailableLanguages,
  getTafsirResources,
  getTranslationLanguages,
  getTranslationResources,
} from "../../../api/quran";
import "./quran.css";
import QURAN_UI_LABELS from "./quranLabels";
import surahBannerOrnament from "./surah-banner-ornament.svg";
import QuranAudioDock from "./components/QuranAudioDock";
import QuranContentPopup from "./components/QuranContentPopup";
import QuranContextMenu from "./components/QuranContextMenu";
import QuranCommandsScroller from "./components/QuranCommandsScroller";
import QuranCommandsTop from "./components/QuranCommandsTop";
import QuranQuickPlayButton from "./components/QuranQuickPlayButton";
import QuranRecitationControls from "./components/QuranRecitationControls";
import QuranPageHeader from "./components/QuranPageHeader";
import QuranPageViewport from "./components/QuranPageViewport";
import QuranSearchOverlay from "./components/QuranSearchOverlay";
import QuranSettingsOverlay from "./components/QuranSettingsOverlay";
import QuranSurahBanner from "./components/QuranSurahBanner";
import QuranSurahOverlay from "./components/QuranSurahOverlay";
import QuranVerseLines from "./components/QuranVerseLines";

const MAX_PAGE = 604;
const QCF_FONT_BASE = "https://verses.quran.foundation/fonts/quran/hafs/v2/woff2";
const UTHMANIC_FONT = "UthmanicHafs, serif";
const SWIPE_THRESHOLD = 50;
const TAP_MOVE_TOLERANCE = 10;
const GESTURE_CANCEL_TOLERANCE = 18;
const SWIPE_PREVIEW_GAP_PX = 14;
const SWIPE_PREVIEW_SETTLE_MS = 170;
const SWIPE_DRAG_MULTIPLIER = 2.05;
const SWIPE_PREVIEW_COMMIT_RATIO = 0.24;
const NATIVE_SWIPE_FINISH_MS = 220;
const VERSE_LONG_PRESS_MS = 450;
const ENABLE_MOBILE_LINE_FIT = false;
const QURAN_USER_SETTINGS_STORAGE_KEY = "quran_user_settings";

const loadedFontFamilies = new Set();

const parseVerseKey = (verseKey) => {
  const [chapterPart, ayahPart] = String(verseKey || "").trim().split(":");
  const chapter = Number(chapterPart);
  const ayah = Number(ayahPart);

  if (!Number.isInteger(chapter) || chapter < 1 || !Number.isInteger(ayah) || ayah < 1) {
    return null;
  }

  return { chapter, ayah };
};

const loadQcfFontForPage = async (pageNumber) => {
  if (!Number.isFinite(pageNumber) || pageNumber < 1 || pageNumber > MAX_PAGE) {
    return null;
  }

  const family = `p${pageNumber}-v2`;
  if (loadedFontFamilies.has(family)) {
    return family;
  }

  const fontFace = new FontFace(family, `url('${QCF_FONT_BASE}/p${pageNumber}.woff2')`);
  fontFace.display = "block";
  await fontFace.load();
  document.fonts.add(fontFace);
  loadedFontFamilies.add(family);

  return family;
};

const groupWordsByLine = (words) => {
  const lines = new Map();

  words.forEach((word) => {
    const line = Number(word.lineNumber) || 0;
    if (!lines.has(line)) {
      lines.set(line, []);
    }
    lines.get(line).push(word);
  });

  return Array.from(lines.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([lineNumber, lineWords]) => ({ lineNumber, words: lineWords }));
};

const truncateLabel = (value, maxLength = 30) => {
  const text = String(value || "").trim();
  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength - 1)}…`;
};

const prioritizeLanguages = (languages, preferredCodes) => {
  const byCode = new Map();
  (languages || []).forEach((lang) => {
    const code = String(lang?.code || "").toLowerCase();
    if (!code || byCode.has(code)) {
      return;
    }
    byCode.set(code, lang);
  });

  const preferred = preferredCodes
    .map((code) => String(code || "").toLowerCase())
    .map((code) => byCode.get(code))
    .filter(Boolean);

  const rest = Array.from(byCode.values()).filter(
    (lang) => !preferred.some((item) => item.code === lang.code)
  );

  return [...preferred, ...rest];
};

const buildRecitationCacheKey = (verseKey, resourceId) => `${String(verseKey || "").trim()}::${Number(resourceId) || 0}`;

const formatTime = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  const mins = Math.floor(total / 60);
  const secs = Math.floor(total % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const createSwipePreviewState = () => ({
  active: false,
  direction: "",
  targetPage: null,
  offsetX: 0,
  settling: false,
});

const getTouchPointByIdentifier = (event, touchIdentifier, preferChanged = false) => {
  const touches = Array.from(event?.touches || []);
  const changedTouches = Array.from(event?.changedTouches || []);

  const pickById = (list) =>
    Number.isInteger(touchIdentifier)
      ? list.find((touch) => Number(touch?.identifier) === Number(touchIdentifier)) || null
      : null;

  const fromChanged = pickById(changedTouches);
  const fromTouches = pickById(touches);

  if (preferChanged) {
    return fromChanged || fromTouches || changedTouches[0] || touches[0] || null;
  }

  return fromTouches || fromChanged || touches[0] || changedTouches[0] || null;
};

const buildPagePresentation = ({ pageNumber, pageData, chapters }) => {
  const surahNames = Array.isArray(pageData?.surahNames) ? pageData.surahNames : [];
  const juzNumbers = Array.isArray(pageData?.juzNumbers) ? pageData.juzNumbers : [];

  const surahLabel =
    surahNames.length === 0
      ? "غير معروف"
      : surahNames.length === 1
        ? surahNames[0]
        : surahNames.length === 2
          ? `${surahNames[0]} / ${surahNames[1]}`
          : `${surahNames[0]} ... ${surahNames[surahNames.length - 1]}`;

  const juzLabel =
    juzNumbers.length === 0
      ? "غير معروف"
      : juzNumbers.length === 1
        ? String(juzNumbers[0])
        : `${juzNumbers[0]}-${juzNumbers[juzNumbers.length - 1]}`;

  const chapterStartingOnPage =
    Array.isArray(chapters) && chapters.length > 0
      ? chapters.find((chapter) => Number(chapter?.pages?.[0]) === Number(pageNumber)) || null
      : null;

  const chapterName = String(chapterStartingOnPage?.name || "").trim();
  const surahBannerLabel = chapterName
    ? chapterName.startsWith("سورة")
      ? chapterName
      : `سورة ${chapterName}`
    : "";

  return {
    surahLabel,
    juzLabel,
    surahBannerLabel,
    showSurahBasmala: Boolean(chapterStartingOnPage?.bismillahPre),
    pageNumber,
  };
};

const shouldCenterMobilePagePanel = (pageNumber) => pageNumber === 1 || pageNumber === 2;
const getPageNumberParityClass = (pageNumber) => (Number(pageNumber) % 2 === 0 ? "quran-bottom-page-number--even" : "quran-bottom-page-number--odd");

const QuranPage = () => {
  const [contextMenu, setContextMenu] = useState({
    open: false,
    x: 0,
    y: 0,
    verseKey: "",
    verticalPlacement: "top",
    horizontalPlacement: "center",
  });
  const [quickPlayPrompt, setQuickPlayPrompt] = useState({
    open: false,
    x: 0,
    y: 0,
    verseKey: "",
  });
  const [fatihaBasmalaWords, setFatihaBasmalaWords] = useState([]);
  const [commandsVisible, setCommandsVisible] = useState(false);
  const [chapters, setChapters] = useState([]);
  const [surahMenuOpen, setSurahMenuOpen] = useState(false);
  const [surahOverlayOpen, setSurahOverlayOpen] = useState(false);
  const [pageSearchOpen, setPageSearchOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [uiTheme, setUiTheme] = useState("light");
  const [uiLanguage, setUiLanguage] = useState("fr");
  const [verseFontScale, setVerseFontScale] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotalResults, setSearchTotalResults] = useState(0);
  const [searchHighlightedVerseKey, setSearchHighlightedVerseKey] = useState("");
  const [contentPopup, setContentPopup] = useState({
    open: false,
    type: "translation",
    title: "",
    verseKey: "",
    loading: false,
    text: "",
    audioUrl: "",
    error: "",
    sourceId: null,
    sourceBook: "",
    sourceAuthor: "",
  });
  const [translationLanguages, setTranslationLanguages] = useState([]);
  const [tafsirLanguages, setTafsirLanguages] = useState([]);
  const [translationResources, setTranslationResources] = useState([]);
  const [translationVerseWords, setTranslationVerseWords] = useState([]);
  const [translationLanguageCode, setTranslationLanguageCode] = useState("fr");
  const [translationResourceId, setTranslationResourceId] = useState(null);
  const [translationOptionsLoading, setTranslationOptionsLoading] = useState(false);
  const [recitationResources, setRecitationResources] = useState([]);
  const [recitationResourceId, setRecitationResourceId] = useState(null);
  const [recitationOptionsLoading, setRecitationOptionsLoading] = useState(false);
  const [recitationMode, setRecitationMode] = useState("ayah");
  const [recitationAnchorVerseKey, setRecitationAnchorVerseKey] = useState("");
  const [recitationPlaylist, setRecitationPlaylist] = useState([]);
  const [recitationPlaylistIndex, setRecitationPlaylistIndex] = useState(0);
  const [recitationSessionActive, setRecitationSessionActive] = useState(false);
  const [recitationSessionLoading, setRecitationSessionLoading] = useState(false);
  const [recitationCurrentVerseKey, setRecitationCurrentVerseKey] = useState("");
  const [recitationCurrentAudioUrl, setRecitationCurrentAudioUrl] = useState("");
  const [recitationCurrentReaderLabel, setRecitationCurrentReaderLabel] = useState("");
  const [recitationIsPlaying, setRecitationIsPlaying] = useState(false);
  const [recitationAudioDockVisible, setRecitationAudioDockVisible] = useState(false);
  const [audioDockHiddenByScreenTap, setAudioDockHiddenByScreenTap] = useState(false);
  const [recitationCurrentTime, setRecitationCurrentTime] = useState(0);
  const [recitationDuration, setRecitationDuration] = useState(0);
  const [recitationRate, setRecitationRate] = useState(1);
  const [bookMenuOpen, setBookMenuOpen] = useState(false);
  const [translationLanguageMenuOpen, setTranslationLanguageMenuOpen] = useState(false);
  const [recitationReaderMenuOpen, setRecitationReaderMenuOpen] = useState(false);
  const [tafsirLanguageCode, setTafsirLanguageCode] = useState("ar");
  const [tafsirResources, setTafsirResources] = useState([]);
  const [tafsirResourceId, setTafsirResourceId] = useState(null);
  const [tafsirOptionsLoading, setTafsirOptionsLoading] = useState(false);
  const [tafsirLanguageMenuOpen, setTafsirLanguageMenuOpen] = useState(false);
  const [tafsirBookMenuOpen, setTafsirBookMenuOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 900
  );
  const [page, setPage] = useState(1);
  const [pageData, setPageData] = useState({ words: [], totalVerses: 0, surahNames: [], juzNumbers: [] });
  const [loadedPages, setLoadedPages] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageTransitionClass, setPageTransitionClass] = useState("");
  const [swipePreview, setSwipePreview] = useState(createSwipePreviewState);
  const [adjacentPageData, setAdjacentPageData] = useState({ prev: null, next: null });
  const [mobileFitScale, setMobileFitScale] = useState(1);
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const touchMovedRef = useRef(false);
  const swipeGestureActiveRef = useRef(false);
  const swipeSourceRef = useRef(null);
  const swipeTouchActiveRef = useRef(false);
  const swipeMouseActiveRef = useRef(false);
  const swipeTouchIdentifierRef = useRef(null);
  const verseTapHandledRef = useRef(false);
  const versePressTimerRef = useRef(null);
  const verseLongPressTriggeredRef = useRef(false);
  const versePressMetaRef = useRef({ x: 0, y: 0, verseKey: "" });
  const linesRef = useRef(null);
  const swipeViewportRef = useRef(null);
  const mushafStageRef = useRef(null);
  const mushafFrameRef = useRef(null);
  const bookMenuRef = useRef(null);
  const translationLanguageMenuRef = useRef(null);
  const recitationReaderMenuRef = useRef(null);
  const recitationAudioRef = useRef(null);
  const previousPageRef = useRef(1);
  const pageTransitionTimerRef = useRef(null);
  const swipePreviewTimerRef = useRef(null);
  const swipeTargetPageRef = useRef(null);
  const swipePreviewRef = useRef(createSwipePreviewState());
  const mouseMoveWindowListenerRef = useRef(null);
  const mouseUpWindowListenerRef = useRef(null);
  const touchMoveWindowListenerRef = useRef(null);
  const touchEndWindowListenerRef = useRef(null);
  const touchCancelWindowListenerRef = useRef(null);
  const nativeSwipeScrollerRef = useRef(null);
  const nativeSwipeScrollTimerRef = useRef(null);
  const nativeSwipeFinishTimerRef = useRef(null);
  const nativeSwipeIgnoreScrollRef = useRef(false);
  const nativeSwipeTouchStartXRef = useRef(null);
  const nativeSwipeMouseStartXRef = useRef(null);
  const nativeSwipeMouseStartScrollLeftRef = useRef(null);
  const nativeSwipePointerMovedRef = useRef(false);
  const pageDataCacheRef = useRef(new Map());
  const pageFetchPromiseCacheRef = useRef(new Map());
  const recitationVersePageCacheRef = useRef(new Map());
  const recitationAudioCacheRef = useRef(new Map());
  const recitationNextPreparedRef = useRef(null);
  const recitationPlaybackStartedRef = useRef(false);
  const recitationPendingAutoPlayRef = useRef(false);
  const searchInputRef = useRef(null);
  const surahOverlayItemRefs = useRef(new Map());
  const tafsirLanguageMenuRef = useRef(null);
  const tafsirBookMenuRef = useRef(null);
  const bookMenuSuppressUntilRef = useRef(0);
  const recitationReaderMenuSuppressUntilRef = useRef(0);
  const tafsirBookMenuSuppressUntilRef = useRef(0);
  const initialUserSettingsRef = useRef(null);

  const canRecitationPrev =
    recitationSessionActive && (recitationMode === "chapter" || recitationMode === "quran") && recitationPlaylistIndex > 0;
  const canRecitationNext =
    recitationSessionActive &&
    (recitationMode === "chapter" || recitationMode === "quran") &&
    recitationPlaylistIndex >= 0 &&
    recitationPlaylistIndex < recitationPlaylist.length - 1;

  const firstVerseKeyOnPage = useMemo(() => {
    const first = pageData.words?.find((word) => String(word?.verseKey || "").trim().length > 0);
    return first?.verseKey || "";
  }, [pageData.words]);

  const uiLabels = useMemo(() => QURAN_UI_LABELS[uiLanguage] || QURAN_UI_LABELS.fr, [uiLanguage]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const rawSettings = window.localStorage.getItem(QURAN_USER_SETTINGS_STORAGE_KEY);
      const parsedSettings = rawSettings ? JSON.parse(rawSettings) : null;

      if (!parsedSettings || typeof parsedSettings !== "object") {
        return;
      }

      initialUserSettingsRef.current = parsedSettings;

      const savedTheme = String(parsedSettings.theme || parsedSettings.uiTheme || "").toLowerCase();
      if (savedTheme === "light" || savedTheme === "dark") {
        setUiTheme(savedTheme);
      }

      const savedLanguage = String(parsedSettings.language || parsedSettings.uiLanguage || "").toLowerCase();
      if (savedLanguage === "fr" || savedLanguage === "en" || savedLanguage === "ar") {
        setUiLanguage(savedLanguage);
      }

      const savedPage = Number(parsedSettings.page);
      if (Number.isFinite(savedPage) && savedPage >= 1 && savedPage <= MAX_PAGE) {
        setPage(Math.trunc(savedPage));
      }

      const savedTranslationLang = String(parsedSettings.translationLanguageCode || "").toLowerCase();
      if (savedTranslationLang) {
        setTranslationLanguageCode(savedTranslationLang);
      }

      const savedTranslationResourceId = Number(parsedSettings.translationResourceId);
      if (Number.isFinite(savedTranslationResourceId) && savedTranslationResourceId > 0) {
        setTranslationResourceId(savedTranslationResourceId);
      }

      const savedRecitationResourceId = Number(parsedSettings.recitationResourceId);
      if (Number.isFinite(savedRecitationResourceId) && savedRecitationResourceId > 0) {
        setRecitationResourceId(savedRecitationResourceId);
      }

      const savedRecitationMode = String(parsedSettings.recitationMode || "").toLowerCase();
      if (savedRecitationMode === "ayah" || savedRecitationMode === "chapter" || savedRecitationMode === "quran") {
        setRecitationMode(savedRecitationMode);
      }

      const savedRecitationRate = Number(parsedSettings.recitationRate);
      if (Number.isFinite(savedRecitationRate) && savedRecitationRate > 0) {
        setRecitationRate(savedRecitationRate);
      }

      const savedVerseFontScale = Number(parsedSettings.verseFontScale);
      if (Number.isFinite(savedVerseFontScale) && savedVerseFontScale >= 0.8 && savedVerseFontScale <= 1.35) {
        setVerseFontScale(savedVerseFontScale);
      }

      const savedTafsirLanguage = String(parsedSettings.tafsirLanguageCode || "").toLowerCase();
      if (savedTafsirLanguage) {
        setTafsirLanguageCode(savedTafsirLanguage);
      }

      const savedTafsirResourceId = Number(parsedSettings.tafsirResourceId);
      if (Number.isFinite(savedTafsirResourceId) && savedTafsirResourceId > 0) {
        setTafsirResourceId(savedTafsirResourceId);
      }
    } catch {
      initialUserSettingsRef.current = null;

      // Legacy fallback for users who still have old standalone keys.
      const legacyTheme = String(window.localStorage.getItem("quran_ui_theme") || "").toLowerCase();
      const legacyLanguage = String(window.localStorage.getItem("quran_ui_language") || "").toLowerCase();

      if (legacyTheme === "light" || legacyTheme === "dark") {
        setUiTheme(legacyTheme);
      }

      if (legacyLanguage === "fr" || legacyLanguage === "en" || legacyLanguage === "ar") {
        setUiLanguage(legacyLanguage);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const settingsPayload = {
      theme: uiTheme,
      language: uiLanguage,
      page,
      translationLanguageCode,
      translationResourceId,
      recitationResourceId,
      recitationMode,
      recitationRate,
      verseFontScale,
      tafsirLanguageCode,
      tafsirResourceId,
    };

    window.localStorage.setItem(QURAN_USER_SETTINGS_STORAGE_KEY, JSON.stringify(settingsPayload));
  }, [
    uiTheme,
    uiLanguage,
    page,
    translationLanguageCode,
    translationResourceId,
    recitationResourceId,
    recitationMode,
    recitationRate,
    verseFontScale,
    tafsirLanguageCode,
    tafsirResourceId,
  ]);

  const currentChapterIdOnPage = useMemo(() => {
    const parsed = parseVerseKey(firstVerseKeyOnPage);
    return parsed?.chapter || null;
  }, [firstVerseKeyOnPage]);

  const recitationCurrentChapterLabel = useMemo(() => {
    const parsed = parseVerseKey(recitationCurrentVerseKey);
    if (!parsed) {
      return "";
    }

    const chapter = chapters.find((item) => item.id === parsed.chapter);
    const chapterName = chapter?.name || `${uiLabels.surahWord} ${parsed.chapter}`;
    return `${chapterName} : ${parsed.ayah}`;
  }, [chapters, recitationCurrentVerseKey, uiLabels.surahWord]);

  const chapterStartingOnCurrentPage = useMemo(() => {
    if (!Array.isArray(chapters) || chapters.length === 0) {
      return null;
    }

    return (
      chapters.find((chapter) => {
        const firstPage = Number(chapter?.pages?.[0]);
        return Number.isFinite(firstPage) && firstPage === Number(page);
      }) || null
    );
  }, [chapters, page]);

  const surahBannerLabel = useMemo(() => {
    if (!chapterStartingOnCurrentPage?.name) {
      return "";
    }

    const chapterName = String(chapterStartingOnCurrentPage.name || "").trim();
    if (!chapterName) {
      return "";
    }

    return chapterName.startsWith("سورة") ? chapterName : `سورة ${chapterName}`;
  }, [chapterStartingOnCurrentPage]);

  const showSurahBasmala = useMemo(() => {
    if (!chapterStartingOnCurrentPage) {
      return false;
    }

    return Boolean(chapterStartingOnCurrentPage.bismillahPre);
  }, [chapterStartingOnCurrentPage]);

  const recitationProgressPercent = useMemo(() => {
    if (!recitationDuration) {
      return 0;
    }

    return Math.max(0, Math.min(100, (recitationCurrentTime / recitationDuration) * 100));
  }, [recitationCurrentTime, recitationDuration]);

  const resolvePageData = useCallback(async (pageNumber) => {
    const safePage = Math.max(1, Math.min(MAX_PAGE, Number(pageNumber) || 1));
    const cached = pageDataCacheRef.current.get(safePage);
    if (cached) {
      return cached;
    }

    const existingPromise = pageFetchPromiseCacheRef.current.get(safePage);
    if (existingPromise) {
      return existingPromise;
    }

    const promise = getQuranPage({ page: safePage, mushaf: 1 })
      .then((data) => {
        pageDataCacheRef.current.set(safePage, data);
        return data;
      })
      .finally(() => {
        pageFetchPromiseCacheRef.current.delete(safePage);
      });

    pageFetchPromiseCacheRef.current.set(safePage, promise);
    return promise;
  }, []);

  const ensurePageAndFontsReady = useCallback(async (pageNumber) => {
    const safePage = Math.max(1, Math.min(MAX_PAGE, Number(pageNumber) || 1));
    const data = await resolvePageData(safePage);

    const pageNumbers = Array.from(
      new Set((data?.words || []).map((word) => Number(word.pageNumber)).filter((num) => Number.isFinite(num)))
    );

    await Promise.all(
      pageNumbers.map(async (num) => {
        try {
          await loadQcfFontForPage(num);
        } catch {
          // Non-blocking: fallback font remains available.
        }
      })
    );

    return data;
  }, [resolvePageData]);

  const warmPage = useCallback((pageNumber) => {
    const safePage = Math.max(1, Math.min(MAX_PAGE, Number(pageNumber) || 1));

    void ensurePageAndFontsReady(safePage)
      .catch(() => {
        // Ignore prefetch failures; foreground load handles retries.
      });
  }, [ensurePageAndFontsReady]);

  useLayoutEffect(() => {
    const cachedData = pageDataCacheRef.current.get(page);
    if (!cachedData) {
      return;
    }

    setError("");
    setLoading(false);
    setPageData(cachedData);

    const pageNumbers = Array.from(
      new Set(cachedData.words.map((word) => Number(word.pageNumber)).filter((num) => Number.isFinite(num)))
    );
    setLoadedPages(new Set(pageNumbers));
  }, [page]);

  useEffect(() => {
    let cancelled = false;

    const loadPage = async () => {
      const hasCached = pageDataCacheRef.current.has(page);
      setLoading(!hasCached);
      setError("");

      try {
        const data = await resolvePageData(page);
        if (cancelled) {
          return;
        }

        setPageData(data);

        const pageNumbers = Array.from(
          new Set(data.words.map((word) => Number(word.pageNumber)).filter((num) => Number.isFinite(num)))
        );

        await Promise.all(
          pageNumbers.map(async (pageNumber) => {
            try {
              await loadQcfFontForPage(pageNumber);
            } catch {
              // Keep Unicode fallback if a font file fails.
            }
          })
        );

        if (!cancelled) {
          setLoadedPages(new Set(pageNumbers));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load Quran page.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      cancelled = true;
    };
  }, [page, resolvePageData]);

  useEffect(() => {
    let cancelled = false;

    const loadFatihaBasmala = async () => {
      try {
        const result = await getVerseByKey({ verseKey: "1:1" });
        if (cancelled) {
          return;
        }

        const words = (Array.isArray(result?.words) ? result.words : []).filter(
          (word) => String(word?.charTypeName || "") !== "end"
        );
        setFatihaBasmalaWords(words);

        // Ensure glyph font for verse 1 is available early to match Quran line rendering.
        await loadQcfFontForPage(1);
      } catch {
        if (!cancelled) {
          setFatihaBasmalaWords([]);
        }
      }
    };

    loadFatihaBasmala();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    warmPage(page - 1);
    warmPage(page + 1);
    warmPage(page - 2);
    warmPage(page + 2);
  }, [page, warmPage]);

  useEffect(() => {
    let cancelled = false;

    const readCachedPage = (pageNumber) => {
      if (!Number.isFinite(pageNumber) || pageNumber < 1 || pageNumber > MAX_PAGE) {
        return null;
      }

      return pageDataCacheRef.current.get(pageNumber) || null;
    };

    const syncAdjacentPages = async () => {
      const previousPageNumber = page - 1;
      const nextPageNumber = page + 1;

      setAdjacentPageData({
        prev: readCachedPage(previousPageNumber),
        next: readCachedPage(nextPageNumber),
      });

      const ensurePageReady = async (pageNumber) => {
        if (!Number.isFinite(pageNumber) || pageNumber < 1 || pageNumber > MAX_PAGE) {
          return null;
        }

        return ensurePageAndFontsReady(pageNumber);
      };

      const [prevData, nextData] = await Promise.all([
        ensurePageReady(previousPageNumber),
        ensurePageReady(nextPageNumber),
      ]);

      if (cancelled) {
        return;
      }

      setAdjacentPageData({ prev: prevData, next: nextData });
    };

    void syncAdjacentPages();

    return () => {
      cancelled = true;
    };
  }, [ensurePageAndFontsReady, page]);

  useEffect(() => {
    const previousPage = Number(previousPageRef.current) || page;
    const nextClass = page > previousPage ? "quran-lines--slide-next" : page < previousPage ? "quran-lines--slide-prev" : "";

    if (pageTransitionTimerRef.current) {
      clearTimeout(pageTransitionTimerRef.current);
      pageTransitionTimerRef.current = null;
    }

    if (!nextClass) {
      previousPageRef.current = page;
      return;
    }

    setPageTransitionClass(nextClass);
    pageTransitionTimerRef.current = setTimeout(() => {
      setPageTransitionClass("");
      pageTransitionTimerRef.current = null;
    }, 140);

    previousPageRef.current = page;

    return () => {
      if (pageTransitionTimerRef.current) {
        clearTimeout(pageTransitionTimerRef.current);
        pageTransitionTimerRef.current = null;
      }
    };
  }, [page]);

  useEffect(() => {
    const key = String(searchHighlightedVerseKey || "").trim();
    if (!key) {
      return;
    }

    const anchor = pageData.words?.find((word) => String(word?.verseKey || "").trim() === key);
    if (!anchor) {
      return;
    }

    const timer = setTimeout(() => {
      const node = document.querySelector(`.quran-word[data-verse-key='${key}']`);
      if (!node || typeof node.scrollIntoView !== "function") {
        return;
      }

      const rect = node.getBoundingClientRect();
      const viewportHeight =
        typeof window !== "undefined" && Number.isFinite(window.innerHeight)
          ? window.innerHeight
          : 0;
      const viewportWidth =
        typeof window !== "undefined" && Number.isFinite(window.innerWidth)
          ? window.innerWidth
          : 0;

      // Skip scrolling when the verse is already visible on screen.
      const fullyVisible =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= viewportHeight &&
        rect.right <= viewportWidth;

      if (!fullyVisible) {
        node.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }, 80);

    return () => clearTimeout(timer);
  }, [pageData.words, searchHighlightedVerseKey]);

  useEffect(() => {
    let cancelled = false;

    const syncPageToRecitationVerse = async () => {
      // Only follow recitation while audio is actively playing.
      // This prevents page navigation from being forced back when playback is paused.
      if (!recitationSessionActive || !recitationIsPlaying || !recitationCurrentVerseKey) {
        return;
      }

      const key = String(recitationCurrentVerseKey || "").trim();
      if (!key) {
        return;
      }

      const cachedPage = recitationVersePageCacheRef.current.get(key);
      if (Number.isFinite(cachedPage)) {
        if (!cancelled && cachedPage !== page) {
          setPage(cachedPage);
        }
        return;
      }

      const currentPageFromWords = pageData.words?.find((word) => word?.verseKey === key)?.pageNumber;
      if (Number.isFinite(currentPageFromWords)) {
        recitationVersePageCacheRef.current.set(key, Number(currentPageFromWords));
        if (!cancelled && Number(currentPageFromWords) !== page) {
          setPage(Number(currentPageFromWords));
        }
        return;
      }

      try {
        const resolvedPage = await getVersePageNumber({ verseKey: key, mushaf: 1 });
        recitationVersePageCacheRef.current.set(key, resolvedPage);
        if (!cancelled && resolvedPage !== page) {
          setPage(resolvedPage);
        }
      } catch {
        // Non-blocking: keep current page if lookup fails.
      }
    };

    void syncPageToRecitationVerse();

    return () => {
      cancelled = true;
    };
  }, [page, pageData.words, recitationCurrentVerseKey, recitationIsPlaying, recitationSessionActive]);

  useEffect(() => {
    let cancelled = false;

    const loadTranslationVerseWords = async () => {
      const isTranslationPopup = contentPopup.open && contentPopup.type === "translation";
      const verseKey = String(contentPopup.verseKey || "").trim();

      if (!isTranslationPopup || !verseKey) {
        setTranslationVerseWords([]);
        return;
      }

      const inCurrentPage = (Array.isArray(pageData.words) ? pageData.words : [])
        .filter((word) => String(word?.verseKey || "").trim() === verseKey);

      if (inCurrentPage.length) {
        if (!cancelled) {
          setTranslationVerseWords(inCurrentPage);
        }
        return;
      }

      try {
        const result = await getVerseByKey({ verseKey, mushaf: 1 });
        const remoteWords = (Array.isArray(result?.words) ? result.words : []).filter(
          (word) => String(word?.verseKey || "").trim() === verseKey
        );

        if (!cancelled) {
          setTranslationVerseWords(remoteWords);
        }
      } catch {
        if (!cancelled) {
          setTranslationVerseWords([]);
        }
      }
    };

    void loadTranslationVerseWords();

    return () => {
      cancelled = true;
    };
  }, [contentPopup.open, contentPopup.type, contentPopup.verseKey, pageData.words]);

  useEffect(() => {
    let cancelled = false;

    const loadChapters = async () => {
      try {
        const list = await getChapterList();
        if (!cancelled) {
          setChapters(Array.isArray(list) ? list : []);
        }
      } catch {
        if (!cancelled) {
          setChapters([]);
        }
      }
    };

    loadChapters();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!surahOverlayOpen || !Number.isFinite(currentChapterIdOnPage)) {
      return;
    }

    const node = surahOverlayItemRefs.current.get(Number(currentChapterIdOnPage));
    if (!node || typeof node.scrollIntoView !== "function") {
      return;
    }

    requestAnimationFrame(() => {
      node.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  }, [currentChapterIdOnPage, surahOverlayOpen]);

  useEffect(() => {
    if (!pageSearchOpen) {
      return;
    }

    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [pageSearchOpen]);

  const lines = useMemo(() => groupWordsByLine(pageData.words), [pageData.words]);
    const fatihaBasmalaLines = useMemo(
      () => (Array.isArray(fatihaBasmalaWords) && fatihaBasmalaWords.length > 0 ? groupWordsByLine(fatihaBasmalaWords) : []),
      [fatihaBasmalaWords]
    );
  const swipeTargetData = useMemo(() => {
    if (!swipePreview.active || !Number.isFinite(swipePreview.targetPage)) {
      return null;
    }

    if (Number(swipePreview.targetPage) === Number(page)) {
      return pageData;
    }

    return pageDataCacheRef.current.get(Number(swipePreview.targetPage)) || null;
  }, [page, pageData, swipePreview.active, swipePreview.targetPage]);
  const swipeTargetLines = useMemo(
    () => (swipeTargetData?.words ? groupWordsByLine(swipeTargetData.words) : []),
    [swipeTargetData]
  );
  const nextPageData = adjacentPageData.next;
  const prevPageData = adjacentPageData.prev;
  const nextPageLines = useMemo(() => (nextPageData?.words ? groupWordsByLine(nextPageData.words) : []), [nextPageData]);
  const prevPageLines = useMemo(() => (prevPageData?.words ? groupWordsByLine(prevPageData.words) : []), [prevPageData]);
  const currentPagePresentation = useMemo(
    () => buildPagePresentation({ pageNumber: page, pageData, chapters }),
    [chapters, page, pageData]
  );
  const nextPagePresentation = useMemo(
    () => buildPagePresentation({ pageNumber: page + 1, pageData: nextPageData, chapters }),
    [chapters, nextPageData, page]
  );
  const prevPagePresentation = useMemo(
    () => buildPagePresentation({ pageNumber: page - 1, pageData: prevPageData, chapters }),
    [chapters, page, prevPageData]
  );

  const surahLabel = useMemo(() => {
    const names = Array.isArray(pageData.surahNames) ? pageData.surahNames : [];
    if (names.length === 0) {
      return "غير معروف";
    }
    if (names.length === 1) {
      return names[0];
    }
    if (names.length === 2) {
      return `${names[0]} / ${names[1]}`;
    }
    return `${names[0]} ... ${names[names.length - 1]}`;
  }, [pageData.surahNames]);

  const juzLabel = useMemo(() => {
    const juz = Array.isArray(pageData.juzNumbers) ? pageData.juzNumbers : [];
    if (juz.length === 0) {
      return "غير معروف";
    }
    if (juz.length === 1) {
      return String(juz[0]);
    }
    return `${juz[0]}-${juz[juz.length - 1]}`;
  }, [pageData.juzNumbers]);

  const canPrev = page > 1;
  const canNext = page < MAX_PAGE;
  const swipePreviewTargetReady = swipePreview.active && swipeTargetLines.length > 0;
  const isMobileView = viewportWidth <= 820;
  const commandMenusVisible = commandsVisible;
  const useNativePageSwipe = true;
  const hasRenderablePage = Array.isArray(pageData.words) && pageData.words.length > 0;
  const showInitialLoading = loading && !hasRenderablePage;
  const selectedTranslationLanguage = useMemo(
    () => translationLanguages.find((item) => item.code === translationLanguageCode) || null,
    [translationLanguages, translationLanguageCode]
  );
  const translationPopupDirection = selectedTranslationLanguage?.direction === "rtl" ? "rtl" : "ltr";
  const selectedTafsirLanguage = useMemo(
    () => tafsirLanguages.find((item) => item.code === tafsirLanguageCode) || null,
    [tafsirLanguages, tafsirLanguageCode]
  );
  const tafsirPopupDirection = selectedTafsirLanguage?.direction === "rtl" ? "rtl" : "ltr";
  const translationLanguageOptions = useMemo(
    () => prioritizeLanguages(translationLanguages, ["fr", "en", "ar"]),
    [translationLanguages]
  );
  const tafsirLanguageOptions = useMemo(
    () => {
      const prioritized = prioritizeLanguages(tafsirLanguages, ["ar", "fr", "en"]);
      const arabic = prioritized.find((item) => item.code === "ar") || null;
      const others = prioritized.filter((item) => item.code !== "ar");
      return arabic ? [arabic, ...others] : prioritized;
    },
    [tafsirLanguages]
  );
  const getTranslationTitle = (languageCode) => {
    const code = String(languageCode || "fr").toLowerCase();
    if (code === "fr") {
      return uiLabels.translation;
    }

    return `${uiLabels.translation} (${code.toUpperCase()})`;
  };

  const getTafsirTitle = (languageCode) => {
    const code = String(languageCode || "ar").toLowerCase();
    if (code === "ar") {
      return `${uiLabels.tafsir} (AR)`;
    }

    return `${uiLabels.tafsir} (${code.toUpperCase()})`;
  };

  const getRecitationTitle = (mode = "ayah") =>
    mode === "chapter" ? `${uiLabels.recitation} (${uiLabels.chapterLabel.toLowerCase()})` : uiLabels.recitation;

  const loadRecitationData = async (verseKey, resourceId) => {
    const key = String(verseKey || "").trim();
    const cacheKey = buildRecitationCacheKey(key, resourceId);
    const cached = recitationAudioCacheRef.current.get(cacheKey);
    if (cached) {
      return cached;
    }

    const result = await getVerseRecitation({
      verseKey: key,
      language: "en",
      recitationId: resourceId,
    });

    const readerLabel = [result.reciterName, result.style].filter(Boolean).join(" - ");
    const normalized = {
      verseKey: result.verseKey,
      recitationId: result.recitationId,
      audioUrl: result.audioUrl,
      readerLabel,
    };

    recitationAudioCacheRef.current.set(buildRecitationCacheKey(result.verseKey, result.recitationId), normalized);
    recitationAudioCacheRef.current.set(cacheKey, normalized);
    return normalized;
  };

  const warmNextRecitationFrom = (currentVerseKey, resourceId, modeOverride = null) => {
    const modeToUse = modeOverride || recitationMode;
    if (modeToUse !== "chapter" && modeToUse !== "quran") {
      recitationNextPreparedRef.current = null;
      return;
    }

    const currentIndex = recitationPlaylist.findIndex((item) => item === currentVerseKey);
    const nextIndex = currentIndex + 1;
    const nextVerseKey = currentIndex >= 0 ? recitationPlaylist[nextIndex] : null;

    if (!nextVerseKey) {
      recitationNextPreparedRef.current = null;
      return;
    }

    void loadRecitationData(nextVerseKey, resourceId)
      .then((nextData) => {
        recitationNextPreparedRef.current = nextData;
      })
      .catch(() => {
        // Ignore warmup errors; foreground playback still handles fetch.
      });
  };

  const buildRecitationPlaylist = async (anchorVerseKey, mode) => {
    const anchor = parseVerseKey(anchorVerseKey);
    if (!anchor) {
      return [String(anchorVerseKey || "").trim()].filter(Boolean);
    }

    if (mode !== "chapter" && mode !== "quran") {
      return [`${anchor.chapter}:${anchor.ayah}`];
    }

    if (mode === "quran") {
      const chapterList = chapters.length > 0 ? chapters : await getChapterList();
      if (!Array.isArray(chapterList) || chapterList.length === 0) {
        return [`${anchor.chapter}:${anchor.ayah}`];
      }

      const all = [];
      for (const chapter of chapterList) {
        const chapterId = Number(chapter?.id);
        const versesCount = Number(chapter?.versesCount);
        if (!Number.isInteger(chapterId) || chapterId < 1 || !Number.isInteger(versesCount) || versesCount < 1) {
          continue;
        }

        let fromAyah = 1;
        if (chapterId === anchor.chapter) {
          fromAyah = Math.max(1, anchor.ayah);
        }
        if (chapterId < anchor.chapter) {
          continue;
        }

        for (let ayah = fromAyah; ayah <= versesCount; ayah += 1) {
          all.push(`${chapterId}:${ayah}`);
        }
      }

      return all.length > 0 ? all : [`${anchor.chapter}:${anchor.ayah}`];
    }

    const chapterInfo = await getChapterInfo(anchor.chapter);
    const versesCount = Number(chapterInfo?.versesCount);
    if (!Number.isInteger(versesCount) || versesCount < anchor.ayah) {
      return [`${anchor.chapter}:${anchor.ayah}`];
    }

    const list = [];
    for (let ayah = anchor.ayah; ayah <= versesCount; ayah += 1) {
      list.push(`${anchor.chapter}:${ayah}`);
    }

    return list;
  };

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let frameId = null;
    const onResize = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        setViewportWidth(window.innerWidth);
        setViewportHeight(window.innerHeight);
      });
    };

    window.addEventListener("resize", onResize, { passive: true });
    onResize();

    return () => {
      window.removeEventListener("resize", onResize);
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, []);

  const mobileResponsiveVars = useMemo(() => {
    const desktopAvailableHeight = Math.max(460, viewportHeight - 96 - 68);
    const desktopAvailableWidth = Math.max(640, Math.min(viewportWidth, 1080) - 36);
    const desktopReferenceHeight = 1620;
    const desktopReferenceWidth = 1080;
    const desktopFitScale = isMobileView
      ? 1
      : Math.max(
          0.44,
          Math.min(
            1,
            Math.min(desktopAvailableHeight / desktopReferenceHeight, desktopAvailableWidth / desktopReferenceWidth) * 0.97
          )
        );

    const baseVars = {
      "--quran-desktop-fit-scale": desktopFitScale.toFixed(3),
    };

    if (!isMobileView) {
      return baseVars;
    }

    const width = Math.max(320, Math.min(820, viewportWidth));
    const mobileFontSize = Math.max(18, Math.min(28, width * 0.064));
    const mobileLineHeight = Math.max(1.64, Math.min(1.82, 1.64 + ((width - 320) / 500) * 0.18));
    const mobileMinLineHeight = Math.max(32, Math.min(42, width * 0.108));
    const mobileScaleX = width < 360 ? 0.84 : width < 420 ? 0.88 : 0.92;
    const mobileHeadSize = Math.max(10.5, Math.min(13, width * 0.031));
    const mobileHeadCenterSize = Math.max(9.5, Math.min(12, width * 0.027));

    const fittedFontSize = Math.max(16, Math.min(52, mobileFontSize * mobileFitScale * verseFontScale));

    return {
      ...baseVars,
      "--quran-mobile-font-size": `${mobileFontSize.toFixed(2)}px`,
      "--quran-mobile-font-size-fitted": `${fittedFontSize.toFixed(2)}px`,
      "--quran-mobile-line-height": mobileLineHeight.toFixed(2),
      "--quran-mobile-min-height": `${mobileMinLineHeight.toFixed(2)}px`,
      "--quran-mobile-scale-x": mobileScaleX.toFixed(3),
      "--quran-mobile-head-size": `${mobileHeadSize.toFixed(2)}px`,
      "--quran-mobile-head-center-size": `${mobileHeadCenterSize.toFixed(2)}px`,
    };
  }, [isMobileView, mobileFitScale, verseFontScale, viewportHeight, viewportWidth]);

  useEffect(() => {
    if (!ENABLE_MOBILE_LINE_FIT || !isMobileView || error || !hasRenderablePage) {
      if (!isMobileView) {
        setMobileFitScale(1);
      }

      const root = linesRef.current;
      if (root) {
        root.querySelectorAll(".quran-line").forEach((lineNode) => {
          lineNode.style.fontSize = "";
        });
      }
      return;
    }

    const fitLines = () => {
      const root = linesRef.current;
      if (!root) {
        return;
      }

      const lines = root.querySelectorAll(".quran-line");
      if (!lines.length) {
        return;
      }

      const available = root.clientWidth;
      if (!available) {
        return;
      }

      // Keep a small safety gutter so glyphs never touch/cross borders on very narrow screens.
      const safeAvailable = Math.max(120, available - 8);

      const baseFontVar = getComputedStyle(root).getPropertyValue("--quran-mobile-font-size-fitted");
      const baseFontSize = Number.parseFloat(baseFontVar) || 22;

      let maxRatio = 1;
      lines.forEach((lineNode) => {
        lineNode.style.fontSize = `${baseFontSize}px`;

        let ratio = (lineNode.scrollWidth + 4) / safeAvailable;
        if (ratio > 1.002) {
          const nextFontSize = Math.max(12.5, baseFontSize * (0.972 / ratio));
          lineNode.style.fontSize = `${nextFontSize.toFixed(2)}px`;

          // Second-pass adjustment catches scripts with larger diacritics after first shrink.
          ratio = (lineNode.scrollWidth + 4) / safeAvailable;
          if (ratio > 1.002) {
            const finalFontSize = Math.max(12, nextFontSize * (0.974 / ratio));
            lineNode.style.fontSize = `${finalFontSize.toFixed(2)}px`;
            ratio = (lineNode.scrollWidth + 4) / safeAvailable;
          }
        }

        if (ratio > maxRatio) {
          maxRatio = ratio;
        }
      });

      const nextScale = maxRatio > 1.002 ? Math.max(0.56, Math.min(1, 0.974 / maxRatio)) : 1;
      setMobileFitScale((prev) => (Math.abs(prev - nextScale) > 0.01 ? nextScale : prev));
    };

    const raf1 = requestAnimationFrame(() => {
      fitLines();
      requestAnimationFrame(fitLines);
    });

    return () => cancelAnimationFrame(raf1);
  }, [error, hasRenderablePage, isMobileView, page, pageData.words, loadedPages]);

  const detachMouseWindowListeners = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (mouseMoveWindowListenerRef.current) {
      window.removeEventListener("mousemove", mouseMoveWindowListenerRef.current);
      mouseMoveWindowListenerRef.current = null;
    }

    if (mouseUpWindowListenerRef.current) {
      window.removeEventListener("mouseup", mouseUpWindowListenerRef.current);
      mouseUpWindowListenerRef.current = null;
    }
  }, []);

  const detachTouchWindowListeners = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (touchMoveWindowListenerRef.current) {
      window.removeEventListener("touchmove", touchMoveWindowListenerRef.current);
      touchMoveWindowListenerRef.current = null;
    }

    if (touchEndWindowListenerRef.current) {
      window.removeEventListener("touchend", touchEndWindowListenerRef.current);
      touchEndWindowListenerRef.current = null;
    }

    if (touchCancelWindowListenerRef.current) {
      window.removeEventListener("touchcancel", touchCancelWindowListenerRef.current);
      touchCancelWindowListenerRef.current = null;
    }
  }, []);

  useEffect(() => {
    swipePreviewRef.current = swipePreview;
  }, [swipePreview]);

  useEffect(() => {
    return () => {
      if (swipePreviewTimerRef.current) {
        clearTimeout(swipePreviewTimerRef.current);
        swipePreviewTimerRef.current = null;
      }

      detachMouseWindowListeners();
      detachTouchWindowListeners();

      if (nativeSwipeScrollTimerRef.current) {
        clearTimeout(nativeSwipeScrollTimerRef.current);
        nativeSwipeScrollTimerRef.current = null;
      }

      if (nativeSwipeFinishTimerRef.current) {
        clearTimeout(nativeSwipeFinishTimerRef.current);
        nativeSwipeFinishTimerRef.current = null;
      }
    };
  }, [detachMouseWindowListeners, detachTouchWindowListeners]);

  useEffect(() => {
    if (!useNativePageSwipe || !hasRenderablePage) {
      return;
    }

    const node = nativeSwipeScrollerRef.current;
    if (!node) {
      return;
    }

    const recenter = () => {
      const panelSpan = node.clientWidth + SWIPE_PREVIEW_GAP_PX;
      nativeSwipeIgnoreScrollRef.current = true;
      node.style.scrollBehavior = "auto";
      node.scrollLeft = panelSpan;
      requestAnimationFrame(() => {
        node.style.scrollBehavior = "";
        nativeSwipeIgnoreScrollRef.current = false;
      });
    };

    const frame = requestAnimationFrame(recenter);
    return () => cancelAnimationFrame(frame);
  }, [page, hasRenderablePage, useNativePageSwipe]);

  const onNativeSwipeScroll = useCallback(() => {
    if (!useNativePageSwipe || nativeSwipeIgnoreScrollRef.current) {
      return;
    }

    const node = nativeSwipeScrollerRef.current;
    if (!node) {
      return;
    }

    const panelSpan = node.clientWidth + SWIPE_PREVIEW_GAP_PX;
    const left = node.scrollLeft;

    if (!canNext && left < panelSpan) {
      nativeSwipeIgnoreScrollRef.current = true;
      node.scrollLeft = panelSpan;
      requestAnimationFrame(() => {
        nativeSwipeIgnoreScrollRef.current = false;
      });
      return;
    }

    if (!canPrev && left > panelSpan) {
      nativeSwipeIgnoreScrollRef.current = true;
      node.scrollLeft = panelSpan;
      requestAnimationFrame(() => {
        nativeSwipeIgnoreScrollRef.current = false;
      });
      return;
    }

    if (nativeSwipeScrollTimerRef.current) {
      clearTimeout(nativeSwipeScrollTimerRef.current);
      nativeSwipeScrollTimerRef.current = null;
    }

    if (nativeSwipeFinishTimerRef.current) {
      clearTimeout(nativeSwipeFinishTimerRef.current);
      nativeSwipeFinishTimerRef.current = null;
    }

    nativeSwipeScrollTimerRef.current = setTimeout(() => {
      nativeSwipeScrollTimerRef.current = null;

      const nextTrigger = panelSpan * 0.42;
      const prevTrigger = panelSpan * 1.58;

      if (left < nextTrigger && canNext) {
        nativeSwipeIgnoreScrollRef.current = true;
        node.scrollTo({ left: 0, behavior: "smooth" });
        nativeSwipeFinishTimerRef.current = setTimeout(() => {
          nativeSwipeFinishTimerRef.current = null;
          const targetPage = Math.min(MAX_PAGE, page + 1);
          void ensurePageAndFontsReady(targetPage)
            .catch(() => {
              // Fall back to foreground fetch if preloading fails.
            })
            .finally(() => {
              setPage(targetPage);
            });
        }, NATIVE_SWIPE_FINISH_MS);
        return;
      }

      if (left > prevTrigger && canPrev) {
        nativeSwipeIgnoreScrollRef.current = true;
        node.scrollTo({ left: panelSpan * 2, behavior: "smooth" });
        nativeSwipeFinishTimerRef.current = setTimeout(() => {
          nativeSwipeFinishTimerRef.current = null;
          const targetPage = Math.max(1, page - 1);
          void ensurePageAndFontsReady(targetPage)
            .catch(() => {
              // Fall back to foreground fetch if preloading fails.
            })
            .finally(() => {
              setPage(targetPage);
            });
        }, NATIVE_SWIPE_FINISH_MS);
        return;
      }

      nativeSwipeIgnoreScrollRef.current = true;
      node.scrollTo({ left: panelSpan, behavior: "smooth" });
      nativeSwipeFinishTimerRef.current = setTimeout(() => {
        nativeSwipeFinishTimerRef.current = null;
        nativeSwipeIgnoreScrollRef.current = false;
      }, NATIVE_SWIPE_FINISH_MS);
    }, 110);
  }, [canNext, canPrev, ensurePageAndFontsReady, page, useNativePageSwipe]);

  const onNativeSwipeTouchStart = useCallback((event) => {
    nativeSwipeTouchStartXRef.current = event.touches?.[0]?.clientX ?? null;
    nativeSwipePointerMovedRef.current = false;
  }, []);

  const onNativeSwipeTouchMove = useCallback(
    (event) => {
      const startX = nativeSwipeTouchStartXRef.current;
      const currentX = event.touches?.[0]?.clientX ?? null;
      if (!Number.isFinite(startX) || !Number.isFinite(currentX)) {
        return;
      }

      const deltaX = currentX - startX;
      if (Math.abs(deltaX) > 8) {
        nativeSwipePointerMovedRef.current = true;
      }
      const tryingBlockedFirstPage = !canPrev && deltaX < -8;
      const tryingBlockedLastPage = !canNext && deltaX > 8;

      if (tryingBlockedFirstPage || tryingBlockedLastPage) {
        if (event.cancelable) {
          event.preventDefault();
        }

        const node = nativeSwipeScrollerRef.current;
        if (node) {
          const panelSpan = node.clientWidth + SWIPE_PREVIEW_GAP_PX;
          node.scrollLeft = panelSpan;
        }
      }
    },
    [canNext, canPrev]
  );

  const onNativeSwipeTouchEnd = useCallback(() => {
    nativeSwipeTouchStartXRef.current = null;
  }, []);

  const onNativeSwipeMouseDown = useCallback((event) => {
    nativeSwipeMouseStartXRef.current = event.clientX;
    nativeSwipeMouseStartScrollLeftRef.current = nativeSwipeScrollerRef.current?.scrollLeft ?? null;
    nativeSwipePointerMovedRef.current = false;

    const node = nativeSwipeScrollerRef.current;
    if (node) {
      node.style.scrollSnapType = "none";
      node.style.scrollBehavior = "auto";
    }
  }, []);

  const onNativeSwipeMouseMove = useCallback(
    (event) => {
      if ((event.buttons & 1) !== 1) {
        return;
      }

      const startX = nativeSwipeMouseStartXRef.current;
      const startScrollLeft = nativeSwipeMouseStartScrollLeftRef.current;
      const currentX = event.clientX;
      if (!Number.isFinite(startX) || !Number.isFinite(currentX) || !Number.isFinite(startScrollLeft)) {
        return;
      }

      const deltaX = currentX - startX;
      if (Math.abs(deltaX) > 8) {
        nativeSwipePointerMovedRef.current = true;
      }
      const node = nativeSwipeScrollerRef.current;
      if (node) {
        event.preventDefault();
        node.scrollLeft = startScrollLeft - deltaX;
      }

      const tryingBlockedFirstPage = !canPrev && deltaX < -8;
      const tryingBlockedLastPage = !canNext && deltaX > 8;

      if (tryingBlockedFirstPage || tryingBlockedLastPage) {
        if (node) {
          const panelSpan = node.clientWidth + SWIPE_PREVIEW_GAP_PX;
          node.scrollLeft = panelSpan;
        }
      }
    },
    [canNext, canPrev]
  );

  const onNativeSwipeMouseUp = useCallback(() => {
    nativeSwipeMouseStartXRef.current = null;
    nativeSwipeMouseStartScrollLeftRef.current = null;

    const node = nativeSwipeScrollerRef.current;
    if (node) {
      node.style.scrollSnapType = "";
      node.style.scrollBehavior = "";
    }
  }, []);

  const clearSwipePreview = useCallback(() => {
    if (swipePreviewTimerRef.current) {
      clearTimeout(swipePreviewTimerRef.current);
      swipePreviewTimerRef.current = null;
    }

    swipeTargetPageRef.current = null;
    swipeGestureActiveRef.current = false;
    swipeTouchActiveRef.current = false;
    swipeMouseActiveRef.current = false;
    swipeTouchIdentifierRef.current = null;
    detachMouseWindowListeners();
    detachTouchWindowListeners();
    setSwipePreview(createSwipePreviewState());
  }, [detachMouseWindowListeners, detachTouchWindowListeners]);

  const settleSwipePreview = useCallback(
    (nextOffset, onDone) => {
      if (!swipePreview.active) {
        return;
      }

      if (swipePreviewTimerRef.current) {
        clearTimeout(swipePreviewTimerRef.current);
        swipePreviewTimerRef.current = null;
      }

      setSwipePreview((prev) => ({ ...prev, offsetX: nextOffset, settling: true }));
      swipePreviewTimerRef.current = setTimeout(() => {
        swipePreviewTimerRef.current = null;
        clearSwipePreview();
        if (typeof onDone === "function") {
          onDone();
        }
      }, SWIPE_PREVIEW_SETTLE_MS);
    },
    [clearSwipePreview, swipePreview.active]
  );

  const isInteractiveSurfaceTarget = (target) => {
    if (!(target instanceof Element)) {
      return false;
    }

    return Boolean(
      target.closest(
        ".quran-word, button, input, select, textarea, a, [role='button'], .quran-context-menu, .quran-quick-play-btn, .quran-translation-popup, .quran-surah-overlay"
      )
    );
  };

  const isOverlayControlTarget = (target) => {
    if (!(target instanceof Element)) {
      return false;
    }

    return Boolean(
      target.closest(
        ".quran-commands-top, .quran-commands-recitation, .quran-commands-scroller, .quran-audio-dock, .quran-context-menu, .quran-quick-play-btn, .quran-translation-popup, .quran-surah-overlay, button, input, select, textarea, a, [role='button']"
      )
    );
  };

  const onSurfaceTap = (target) => {
    if (commandsVisible || recitationAudioDockVisible) {
      if (isOverlayControlTarget(target)) {
        return;
      }

      onToggleCommands();
      return;
    }

    if (isInteractiveSurfaceTarget(target)) {
      return;
    }

    if ((recitationIsPlaying || recitationSessionLoading) && recitationSessionActive && !recitationAudioDockVisible) {
      closeQuickPlayPrompt();
      setRecitationAudioDockVisible(true);
      setAudioDockHiddenByScreenTap(false);
      return;
    }

    onToggleCommands();
  };

  const onScreenPointerUp = (event) => {
    if (event.pointerType && event.pointerType !== "mouse" && event.pointerType !== "pen") {
      return;
    }

    if (useNativePageSwipe && nativeSwipePointerMovedRef.current) {
      nativeSwipePointerMovedRef.current = false;
      return;
    }

    onSurfaceTap(event.target);
  };

  const resetSwipeState = useCallback(() => {
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    touchMovedRef.current = false;
    swipeGestureActiveRef.current = false;
    swipeSourceRef.current = null;
    swipeTouchActiveRef.current = false;
    swipeMouseActiveRef.current = false;
    swipeTouchIdentifierRef.current = null;
  }, []);

  const cancelActiveSwipe = useCallback(() => {
    clearVersePressTimer();
    detachMouseWindowListeners();
    detachTouchWindowListeners();

    if (swipePreviewRef.current.active) {
      settleSwipePreview(0);
      return;
    }

    clearSwipePreview();
  }, [clearSwipePreview, detachMouseWindowListeners, detachTouchWindowListeners, settleSwipePreview]);

  const startSwipeDrag = useCallback(
    ({ source, clientX, clientY, touchIdentifier = null }) => {
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
        return;
      }

      if (swipePreviewTimerRef.current) {
        clearTimeout(swipePreviewTimerRef.current);
        swipePreviewTimerRef.current = null;
      }

      detachMouseWindowListeners();
      detachTouchWindowListeners();

      swipeSourceRef.current = source;
      swipeTouchActiveRef.current = source === "touch";
      swipeMouseActiveRef.current = source === "mouse";
      swipeTouchIdentifierRef.current = source === "touch" ? touchIdentifier : null;
      touchStartXRef.current = clientX;
      touchStartYRef.current = clientY;
      touchMovedRef.current = false;
      swipeGestureActiveRef.current = false;
      setSwipePreview(createSwipePreviewState());
    },
    [detachMouseWindowListeners, detachTouchWindowListeners]
  );

  const updateSwipeDrag = useCallback(
    ({ source, clientX, clientY, event }) => {
      if (swipeSourceRef.current !== source) {
        return;
      }

      const startX = touchStartXRef.current;
      const startY = touchStartYRef.current;
      if (!Number.isFinite(startX) || !Number.isFinite(startY) || !Number.isFinite(clientX) || !Number.isFinite(clientY)) {
        return;
      }

      const movedX = Math.abs(clientX - startX);
      const movedY = Math.abs(clientY - startY);

      if (movedX > TAP_MOVE_TOLERANCE || movedY > TAP_MOVE_TOLERANCE) {
        touchMovedRef.current = true;
      }

      if (movedX > GESTURE_CANCEL_TOLERANCE || movedY > GESTURE_CANCEL_TOLERANCE) {
        clearVersePressTimer();
      }

      const horizontalIntent = movedX > GESTURE_CANCEL_TOLERANCE && movedX > movedY + 6;
      if (horizontalIntent) {
        swipeGestureActiveRef.current = true;
      }

      if (!swipeGestureActiveRef.current) {
        return;
      }

      if (event?.cancelable) {
        event.preventDefault();
      }

      const direction = clientX >= startX ? "next" : "prev";
      const targetPage = direction === "next" ? page + 1 : page - 1;
      const canSlide = direction === "next" ? canNext : canPrev;

      if (!canSlide) {
        setSwipePreview(createSwipePreviewState());
        return;
      }

      const viewWidth = swipeViewportRef.current?.clientWidth || viewportWidth || 380;
      const maxOffset = Math.max(160, viewWidth + SWIPE_PREVIEW_GAP_PX);
      const amplifiedDelta = (clientX - startX) * SWIPE_DRAG_MULTIPLIER;
      const offsetX = Math.max(-maxOffset, Math.min(maxOffset, amplifiedDelta));

      if (swipeTargetPageRef.current !== targetPage) {
        swipeTargetPageRef.current = targetPage;
        warmPage(targetPage);
      }

      setSwipePreview({
        active: true,
        direction,
        targetPage,
        offsetX,
        settling: false,
      });
    },
    [canNext, canPrev, page, viewportWidth, warmPage]
  );

  const finishSwipeDrag = ({ source, clientX, clientY, target }) => {
      if (swipeSourceRef.current !== source) {
        return;
      }

      detachMouseWindowListeners();
      detachTouchWindowListeners();

      if (loading) {
        resetSwipeState();
        return;
      }

      if (verseTapHandledRef.current) {
        verseTapHandledRef.current = false;
        resetSwipeState();
        return;
      }

      const startX = touchStartXRef.current;
      const startY = touchStartYRef.current;
      const wasTouchMoved = touchMovedRef.current;
      const activePreview = swipePreviewRef.current;
      resetSwipeState();

      if (!Number.isFinite(startX) || !Number.isFinite(startY) || !Number.isFinite(clientX) || !Number.isFinite(clientY)) {
        onSurfaceTap(target);
        return;
      }

      const deltaX = clientX - startX;
      const deltaY = clientY - startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (activePreview.active) {
        const viewWidth = swipeViewportRef.current?.clientWidth || viewportWidth || 380;
        const absPreviewOffset = Math.abs(activePreview.offsetX);
        const commitSwipe =
          absX >= SWIPE_THRESHOLD || absPreviewOffset >= Math.max(56, viewWidth * SWIPE_PREVIEW_COMMIT_RATIO);

        if (commitSwipe) {
          const direction = activePreview.direction || (deltaX > 0 ? "next" : "prev");
          const canSlide = direction === "next" ? canNext : canPrev;

          if (canSlide) {
            const travel = Math.max(200, viewWidth + SWIPE_PREVIEW_GAP_PX);
            const finalOffset = direction === "next" ? travel : -travel;

            setSwipePreview((prev) => ({ ...prev, direction, targetPage: direction === "next" ? page + 1 : page - 1 }));
            settleSwipePreview(finalOffset, () => {
              if (direction === "next") {
                setPage((prev) => Math.min(MAX_PAGE, prev + 1));
              } else {
                setPage((prev) => Math.max(1, prev - 1));
              }
            });
            return;
          }
        }

        settleSwipePreview(0);
        return;
      }

      if (absX >= SWIPE_THRESHOLD && absX > absY) {
        if (deltaX > 0 && canNext) {
          setPage((prev) => Math.min(MAX_PAGE, prev + 1));
        } else if (deltaX < 0 && canPrev) {
          setPage((prev) => Math.max(1, prev - 1));
        }
        return;
      }

      if (wasTouchMoved) {
        return;
      }

      if (absX > GESTURE_CANCEL_TOLERANCE || absY > GESTURE_CANCEL_TOLERANCE) {
        return;
      }

      if (absX > TAP_MOVE_TOLERANCE || absY > TAP_MOVE_TOLERANCE) {
        return;
      }

      if (absX < SWIPE_THRESHOLD) {
        onSurfaceTap(target);
      }
    };

  const onSwipeTouchStartCapture = (event) => {
    const startTouch = getTouchPointByIdentifier(event, null);
    const identifier = Number.isInteger(Number(startTouch?.identifier)) ? Number(startTouch.identifier) : null;
    startSwipeDrag({ source: "touch", clientX: startTouch?.clientX ?? null, clientY: startTouch?.clientY ?? null, touchIdentifier: identifier });

    if (typeof window !== "undefined") {
      touchMoveWindowListenerRef.current = (nativeEvent) => {
        onSwipeTouchMoveCapture(nativeEvent);
      };
      touchEndWindowListenerRef.current = (nativeEvent) => {
        onSwipeTouchEndCapture(nativeEvent);
      };
      touchCancelWindowListenerRef.current = (nativeEvent) => {
        onSwipeTouchCancelCapture(nativeEvent);
      };
      window.addEventListener("touchmove", touchMoveWindowListenerRef.current, { passive: false });
      window.addEventListener("touchend", touchEndWindowListenerRef.current);
      window.addEventListener("touchcancel", touchCancelWindowListenerRef.current);
    }
  };

  const onSwipeTouchMoveCapture = (event) => {
    const currentTouch = getTouchPointByIdentifier(event, swipeTouchIdentifierRef.current);
    updateSwipeDrag({ source: "touch", clientX: currentTouch?.clientX ?? null, clientY: currentTouch?.clientY ?? null, event });
  };

  const onSwipeTouchEndCapture = (event) => {
    const endTouch = getTouchPointByIdentifier(event, swipeTouchIdentifierRef.current, true);
    finishSwipeDrag({ source: "touch", clientX: endTouch?.clientX ?? null, clientY: endTouch?.clientY ?? null, target: event.target });
  };

  const onSwipeTouchCancelCapture = () => {
    if (swipeSourceRef.current !== "touch") {
      return;
    }

    resetSwipeState();
    cancelActiveSwipe();
  };

  const onSwipeMouseDownCapture = (event) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();
    startSwipeDrag({ source: "mouse", clientX: event.clientX, clientY: event.clientY });

    if (typeof window !== "undefined") {
      mouseMoveWindowListenerRef.current = (nativeEvent) => {
        onSwipeMouseMoveCapture(nativeEvent);
      };
      mouseUpWindowListenerRef.current = (nativeEvent) => {
        onSwipeMouseUpCapture(nativeEvent);
      };
      window.addEventListener("mousemove", mouseMoveWindowListenerRef.current, { passive: false });
      window.addEventListener("mouseup", mouseUpWindowListenerRef.current);
    }
  };

  const onSwipeMouseMoveCapture = (event) => {
    updateSwipeDrag({ source: "mouse", clientX: event.clientX, clientY: event.clientY, event });
  };

  const onSwipeMouseUpCapture = (event) => {
    finishSwipeDrag({ source: "mouse", clientX: event.clientX, clientY: event.clientY, target: event.target });
  };

  const closeQuickPlayPrompt = () => {
    setQuickPlayPrompt({
      open: false,
      x: 0,
      y: 0,
      verseKey: "",
    });
  };

  const openContextMenuAt = ({ clientX, clientY, verseKey }) => {
    if (!verseKey) {
      return;
    }

    const estimatedMenuWidth = 196;
    const estimatedMenuHeight = 190;
    const margin = 12;
    const halfMenuWidth = estimatedMenuWidth / 2;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const canCenterHorizontally =
      clientX >= halfMenuWidth + margin &&
      clientX <= viewportWidth - halfMenuWidth - margin;

    const horizontalPlacement = canCenterHorizontally
      ? "center"
      : clientX < viewportWidth / 2
        ? "right"
        : "left";

    const verticalPlacement = clientY > estimatedMenuHeight + margin ? "top" : "bottom";

    let viewportSafeX = clientX;
    if (horizontalPlacement === "center") {
      viewportSafeX = Math.max(
        halfMenuWidth + margin,
        Math.min(viewportWidth - halfMenuWidth - margin, clientX)
      );
    } else if (horizontalPlacement === "right") {
      viewportSafeX = Math.max(margin, Math.min(viewportWidth - estimatedMenuWidth - margin, clientX));
    } else {
      viewportSafeX = Math.max(estimatedMenuWidth + margin, Math.min(viewportWidth - margin, clientX));
    }

    let viewportSafeY = clientY;
    if (verticalPlacement === "top") {
      viewportSafeY = Math.max(estimatedMenuHeight + margin, Math.min(viewportHeight - margin, clientY));
    } else {
      viewportSafeY = Math.max(margin, Math.min(viewportHeight - estimatedMenuHeight - margin, clientY));
    }

    closeQuickPlayPrompt();
    setContextMenu({
      open: true,
      x: viewportSafeX,
      y: viewportSafeY,
      verseKey,
      verticalPlacement,
      horizontalPlacement,
    });
  };

  const openQuickPlayAt = ({ clientX, clientY, verseKey }) => {
    if (!verseKey) {
      return;
    }

    // Use bottom command controls instead of floating quick-play at tap position.
    closeContextMenu();
    closeQuickPlayPrompt();
    setRecitationAnchorVerseKey(String(verseKey || "").trim());
    setCommandsVisible(true);
  };

  function clearVersePressTimer() {
    if (versePressTimerRef.current) {
      clearTimeout(versePressTimerRef.current);
      versePressTimerRef.current = null;
    }
  }

  const onVersePointerDown = (event, verseKey) => {
    event.stopPropagation();
    if (!verseKey) {
      return;
    }

    verseLongPressTriggeredRef.current = false;
    const clientX = Number(event.clientX) || 0;
    const clientY = Number(event.clientY) || 0;
    versePressMetaRef.current = { x: clientX, y: clientY, verseKey };

    clearVersePressTimer();
    versePressTimerRef.current = setTimeout(() => {
      verseLongPressTriggeredRef.current = true;
      const meta = versePressMetaRef.current;
      openContextMenuAt({ clientX: meta.x, clientY: meta.y, verseKey: meta.verseKey });
    }, VERSE_LONG_PRESS_MS);
  };

  const onVersePointerUp = (event, verseKey) => {
    event.stopPropagation();
    clearVersePressTimer();

    if (event.pointerType === "touch" && touchMovedRef.current) {
      return;
    }

    if (!verseKey || verseLongPressTriggeredRef.current) {
      return;
    }

    verseTapHandledRef.current = true;

    if (commandsVisible || recitationAudioDockVisible) {
      onToggleCommands();
      return;
    }

    if ((recitationIsPlaying || recitationSessionLoading) && recitationSessionActive && !recitationAudioDockVisible) {
      closeQuickPlayPrompt();
      setRecitationAudioDockVisible(true);
      setAudioDockHiddenByScreenTap(false);
      return;
    }

    openQuickPlayAt({
      clientX: Number(event.clientX) || versePressMetaRef.current.x,
      clientY: Number(event.clientY) || versePressMetaRef.current.y,
      verseKey,
    });
  };

  const onVersePointerCancel = () => {
    clearVersePressTimer();
  };

  const closeContextMenu = () => {
    clearVersePressTimer();
    setContextMenu({
      open: false,
      x: 0,
      y: 0,
      verseKey: "",
      verticalPlacement: "top",
      horizontalPlacement: "center",
    });
  };

  const closeContentPopup = () => {
    setContentPopup({
      open: false,
      type: "translation",
      title: "",
      verseKey: "",
      loading: false,
      text: "",
      audioUrl: "",
      error: "",
      sourceId: null,
      sourceBook: "",
      sourceAuthor: "",
    });
  };

  const tryPlayRecitationAudio = useCallback(() => {
    const audio = recitationAudioRef.current;
    if (!audio) {
      return false;
    }

    recitationPendingAutoPlayRef.current = false;
    recitationPlaybackStartedRef.current = true;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        // User gesture may still be required on some browsers.
      });
    }

    return true;
  }, []);

  const stopRecitationSession = () => {
    const audio = recitationAudioRef.current;
    if (audio) {
      audio.pause();
      try {
        audio.currentTime = 0;
      } catch {
        // Ignore seek errors on unavailable streams.
      }
    }

    setRecitationIsPlaying(false);
    setRecitationCurrentTime(0);
    setRecitationDuration(0);
    setRecitationSessionActive(false);
    setRecitationSessionLoading(false);
    setRecitationCurrentAudioUrl("");
    setAudioDockHiddenByScreenTap(false);
    recitationNextPreparedRef.current = null;
    recitationPlaybackStartedRef.current = false;
    recitationPendingAutoPlayRef.current = false;
  };

  const loadTranslationOptions = async (languageCode, preferredResourceId = null) => {
    const lang = String(languageCode || "fr").toLowerCase();
    setTranslationOptionsLoading(true);

    try {
      const resources = await getTranslationResources(lang);
      const nextResourceId =
        preferredResourceId && resources.some((item) => item.id === Number(preferredResourceId))
          ? Number(preferredResourceId)
          : resources[0]?.id ?? null;

      setTranslationLanguageCode(lang);
      setTranslationResources(resources);
      setTranslationResourceId(nextResourceId);
      return { languageCode: lang, resourceId: nextResourceId };
    } finally {
      setTranslationOptionsLoading(false);
    }
  };

  const loadRecitationOptions = async (preferredResourceId = null) => {
    setRecitationOptionsLoading(true);

    try {
      const resources = await getRecitationResources("en");
      const nextResourceId =
        preferredResourceId && resources.some((item) => item.id === Number(preferredResourceId))
          ? Number(preferredResourceId)
          : resources[0]?.id ?? null;

      setRecitationResources(resources);
      setRecitationResourceId(nextResourceId);
      return { resourceId: nextResourceId };
    } finally {
      setRecitationOptionsLoading(false);
    }
  };

  const requestTranslationForVerse = async (verseKey, languageCode, resourceId) => {
    const key = String(verseKey || "").trim();
    if (!key) {
      return;
    }

    setBookMenuOpen(false);

    const title = getTranslationTitle(languageCode);

    setContentPopup((prev) => ({
      ...prev,
      open: true,
      type: "translation",
      title,
      verseKey: key,
      loading: true,
      text: "",
      audioUrl: "",
      error: "",
      sourceId: Number.isFinite(Number(resourceId)) ? Number(resourceId) : null,
      sourceBook: "",
      sourceAuthor: "",
    }));

    try {
      const result = await getVerseTranslation({
        verseKey: key,
        language: languageCode,
        translationId: resourceId,
      });

      setContentPopup({
        open: true,
        type: "translation",
        title,
        verseKey: result.verseKey,
        loading: false,
        text: result.text,
        audioUrl: "",
        error: "",
        sourceId: result.translationId,
        sourceBook: result.book || "",
        sourceAuthor: result.author || "",
      });

      if (Number.isFinite(Number(result.translationId))) {
        setTranslationResourceId(Number(result.translationId));
      }
    } catch (err) {
      setContentPopup((prev) => ({
        ...prev,
        open: true,
        type: "translation",
        title,
        verseKey: key,
        loading: false,
        text: "",
        audioUrl: "",
        error: err?.message || uiLabels.translationLoadError,
        sourceBook: "",
        sourceAuthor: "",
      }));
    }
  };

  const onSelectFrenchTranslation = () => {
    const selectedVerse = contextMenu.verseKey;
    closeContextMenu();

    if (!selectedVerse) {
      return;
    }

    requestTranslationForVerse(selectedVerse, translationLanguageCode, translationResourceId);
  };

  const onChangeTranslationLanguage = async (event) => {
    const nextLanguage = event?.target?.value || event;
    setBookMenuOpen(false);
    setTranslationLanguageMenuOpen(false);
    const next = await loadTranslationOptions(nextLanguage);

    if (contentPopup.open && contentPopup.type === "translation" && contentPopup.verseKey) {
      requestTranslationForVerse(contentPopup.verseKey, next.languageCode, next.resourceId);
    }
  };

  const onSelectTranslationBook = (resourceId) => {
    const nextResourceId = Number(resourceId) || null;
    bookMenuSuppressUntilRef.current = Date.now() + 350;
    setTranslationResourceId(nextResourceId);
    setBookMenuOpen(false);

    if (contentPopup.open && contentPopup.type === "translation" && contentPopup.verseKey) {
      requestTranslationForVerse(contentPopup.verseKey, translationLanguageCode, nextResourceId);
    }
  };

  const onSelectTranslationLanguage = async (languageCode) => {
    await onChangeTranslationLanguage(languageCode);
  };

  const requestRecitationForVerse = async (
    verseKey,
    resourceId,
    mode = recitationMode,
    options = {}
  ) => {
    const smoothTransition = Boolean(options.smoothTransition);
    const autoplayAfterLoad = Boolean(options.autoplayAfterLoad);
    const key = String(verseKey || "").trim();
    if (!key) {
      return;
    }

    setRecitationReaderMenuOpen(false);
    recitationPendingAutoPlayRef.current = autoplayAfterLoad;
    setRecitationSessionLoading(!smoothTransition);

    try {
      const result = await loadRecitationData(key, resourceId);
      const nextVerseKey = result.verseKey || key;

      setRecitationSessionActive(true);
      setRecitationSessionLoading(false);
      setRecitationCurrentVerseKey(nextVerseKey);
      setRecitationCurrentAudioUrl(result.audioUrl);
      setRecitationCurrentReaderLabel(result.readerLabel || "");

      if (Number.isFinite(Number(result.recitationId))) {
        setRecitationResourceId(Number(result.recitationId));
      }

      warmNextRecitationFrom(nextVerseKey, Number(result.recitationId) || Number(resourceId), mode);
    } catch (err) {
      setRecitationSessionLoading(false);
      recitationPendingAutoPlayRef.current = false;
    }
  };

  const startRecitationPlayback = async ({
    anchorVerseKey,
    mode,
    resourceId,
    preferredCurrentVerseKey = null,
    autoplayOnStart = false,
  }) => {
    const anchor = String(anchorVerseKey || "").trim();
    if (!anchor) {
      return;
    }

    try {
      const playlist = await buildRecitationPlaylist(anchor, mode);
      const normalizedPlaylist = Array.isArray(playlist) ? playlist.filter(Boolean) : [];
      const fallbackKey = preferredCurrentVerseKey || anchor;
      const nextIndexCandidate = normalizedPlaylist.findIndex((item) => item === preferredCurrentVerseKey);
      const nextIndex = nextIndexCandidate >= 0 ? nextIndexCandidate : 0;
      const currentVerseKey = normalizedPlaylist[nextIndex] || fallbackKey;

      setRecitationMode(mode);
      setRecitationAnchorVerseKey(anchor);
      setRecitationPlaylist(normalizedPlaylist.length > 0 ? normalizedPlaylist : [currentVerseKey]);
      setRecitationPlaylistIndex(nextIndex);
      await requestRecitationForVerse(currentVerseKey, resourceId, mode, {
        smoothTransition: false,
        autoplayAfterLoad: autoplayOnStart,
      });
    } catch (err) {
      setRecitationSessionLoading(false);
    }
  };

  const onChangeRecitationMode = (nextMode) => {
    const mode = nextMode === "chapter" ? "chapter" : "ayah";
    setRecitationMode(mode);

    if (contentPopup.open && contentPopup.type === "recitation") {
      setContentPopup((prev) => ({
        ...prev,
        title: getRecitationTitle(mode),
      }));
    }
  };

  const onChangeRecitationReader = async (event) => {
    const nextResourceId = Number(event?.target?.value || event) || null;
    recitationReaderMenuSuppressUntilRef.current = Date.now() + 350;
    setRecitationResourceId(nextResourceId);
    setRecitationReaderMenuOpen(false);

    if (recitationSessionActive && recitationCurrentVerseKey) {
      requestRecitationForVerse(recitationCurrentVerseKey, nextResourceId, recitationMode, {
        smoothTransition: true,
        autoplayAfterLoad: recitationIsPlaying,
      });
    }
  };

  const onRecitationPrevious = async () => {
    if (!canRecitationPrev) {
      return;
    }

    const nextIndex = recitationPlaylistIndex - 1;
    if (nextIndex < 0) {
      return;
    }

    const nextVerseKey = recitationPlaylist[nextIndex];
    if (!nextVerseKey) {
      return;
    }

    setRecitationPlaylistIndex(nextIndex);
    await requestRecitationForVerse(nextVerseKey, recitationResourceId, recitationMode, {
      smoothTransition: true,
      autoplayAfterLoad: recitationPlaybackStartedRef.current,
    });
  };

  const onRecitationNext = async () => {
    if (!canRecitationNext) {
      return;
    }

    const nextIndex = recitationPlaylistIndex + 1;
    if (nextIndex >= recitationPlaylist.length) {
      return;
    }

    const nextVerseKey = recitationPlaylist[nextIndex];
    if (!nextVerseKey) {
      return;
    }

    setRecitationPlaylistIndex(nextIndex);
    await requestRecitationForVerse(nextVerseKey, recitationResourceId, recitationMode, {
      smoothTransition: true,
      autoplayAfterLoad: recitationPlaybackStartedRef.current,
    });
  };

  const onRecitationEnded = () => {
    if (!canRecitationNext) {
      return;
    }

    if (!recitationPlaybackStartedRef.current) {
      return;
    }

    const nextIndex = recitationPlaylistIndex + 1;
    const nextVerseKey = recitationPlaylist[nextIndex];
    const prepared = recitationNextPreparedRef.current;

    if (nextVerseKey && prepared && prepared.verseKey === nextVerseKey) {
      setRecitationPlaylistIndex(nextIndex);
      recitationPendingAutoPlayRef.current = true;
      setRecitationCurrentVerseKey(prepared.verseKey);
      setRecitationCurrentAudioUrl(prepared.audioUrl);
      setRecitationCurrentReaderLabel(prepared.readerLabel || recitationCurrentReaderLabel || "");
      warmNextRecitationFrom(prepared.verseKey, prepared.recitationId || recitationResourceId, recitationMode);
      return;
    }

    void onRecitationNext();
  };

  const onRecitationPlay = () => {
    recitationPlaybackStartedRef.current = true;
    setRecitationIsPlaying(true);
  };

  const onRecitationPause = () => {
    setRecitationIsPlaying(false);
  };

  const onRecitationTimeUpdate = () => {
    const audio = recitationAudioRef.current;
    if (!audio) {
      return;
    }

    setRecitationCurrentTime(Number(audio.currentTime) || 0);
  };

  const onRecitationLoadedMetadata = () => {
    const audio = recitationAudioRef.current;
    if (!audio) {
      return;
    }

    setRecitationDuration(Number(audio.duration) || 0);
    setRecitationCurrentTime(Number(audio.currentTime) || 0);
    audio.playbackRate = recitationRate;
  };

  const onRecitationSeek = (nextProgress) => {
    const audio = recitationAudioRef.current;
    if (!audio) {
      return;
    }

    const progress = Math.max(0, Math.min(1, Number(nextProgress) || 0));
    const duration = Number(audio.duration) || recitationDuration;
    if (!duration) {
      return;
    }

    audio.currentTime = duration * progress;
    setRecitationCurrentTime(audio.currentTime);
  };

  const onToggleRecitationRate = () => {
    const rates = [0.75, 1, 1.25, 1.5];
    const currentIndex = rates.findIndex((rate) => Math.abs(rate - recitationRate) < 0.01);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setRecitationRate(nextRate);

    const audio = recitationAudioRef.current;
    if (audio) {
      audio.playbackRate = nextRate;
    }
  };

  const onTogglePlayPause = () => {
    const audio = recitationAudioRef.current;
    if (!audio || !recitationSessionActive) {
      return;
    }

    if (recitationIsPlaying) {
      audio.pause();
      return;
    }

    onHeaderPlay();
  };

  const onRecitationLoadedData = () => {
    if (!recitationPendingAutoPlayRef.current) {
      return;
    }

    tryPlayRecitationAudio();
  };

  const onStartRecitationFromPopup = () => {
    const anchorVerse = String(contentPopup.verseKey || recitationAnchorVerseKey || "").trim();
    if (!anchorVerse) {
      return;
    }

    recitationPlaybackStartedRef.current = true;
    recitationPendingAutoPlayRef.current = true;
    setRecitationAudioDockVisible(true);
    setAudioDockHiddenByScreenTap(false);
    closeContentPopup();

    void startRecitationPlayback({
      anchorVerseKey: anchorVerse,
      mode: recitationMode,
      resourceId: recitationResourceId,
      preferredCurrentVerseKey: anchorVerse,
      autoplayOnStart: true,
    });
  };

  const startRecitationFromVerse = (verseKey, mode = "ayah") => {
    const selectedVerse = String(verseKey || "").trim();
    if (!selectedVerse) {
      return;
    }

    const resolvedMode = mode === "quran" ? "quran" : mode === "chapter" ? "chapter" : "ayah";

    closeQuickPlayPrompt();
    closeContextMenu();
    closeContentPopup();
    setRecitationAudioDockVisible(true);
    setAudioDockHiddenByScreenTap(false);
    setRecitationAnchorVerseKey(selectedVerse);
    setRecitationMode(resolvedMode);
    recitationPlaybackStartedRef.current = true;
    recitationPendingAutoPlayRef.current = true;

    void startRecitationPlayback({
      anchorVerseKey: selectedVerse,
      mode: resolvedMode,
      resourceId: recitationResourceId,
      preferredCurrentVerseKey: selectedVerse,
      autoplayOnStart: true,
    });
  };

  const onQuickPlayRecitation = () => {
    startRecitationFromVerse(quickPlayPrompt.verseKey, "quran");
  };

  useEffect(() => {
    if (!recitationPendingAutoPlayRef.current || !recitationSessionActive || recitationSessionLoading) {
      return;
    }

    const audio = recitationAudioRef.current;
    if (!audio || !recitationCurrentAudioUrl || audio.readyState < 2) {
      return;
    }

    const timer = window.setTimeout(() => {
      tryPlayRecitationAudio();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [recitationCurrentAudioUrl, recitationSessionActive, recitationSessionLoading, tryPlayRecitationAudio]);

  const closeNavigationPanels = () => {
    setPageSearchOpen(false);
    setSurahMenuOpen(false);
    setSurahOverlayOpen(false);
  };

  const closePopupSelectorMenus = () => {
    setBookMenuOpen(false);
    setTranslationLanguageMenuOpen(false);
    setRecitationReaderMenuOpen(false);
    setTafsirLanguageMenuOpen(false);
    setTafsirBookMenuOpen(false);
  };

  const closeCommandMenus = () => {
    setCommandsVisible(false);
    closeNavigationPanels();
  };

  const onToggleCommands = () => {
    const shouldHideNow = commandsVisible || recitationAudioDockVisible;

    if (shouldHideNow) {
      closeCommandMenus();

      if (recitationSessionActive && recitationAudioDockVisible) {
        setRecitationAudioDockVisible(false);
        setAudioDockHiddenByScreenTap(true);
      }
      return;
    }

    setCommandsVisible(true);

    if ((recitationIsPlaying || recitationSessionLoading) && recitationSessionActive && audioDockHiddenByScreenTap) {
      setRecitationAudioDockVisible(true);
      setAudioDockHiddenByScreenTap(false);
    }
  };

  const onOpenTextSearch = () => {
    setPageSearchOpen(true);
    setSurahMenuOpen(false);
    setSurahOverlayOpen(false);
  };

  const onOpenSettingsPanel = () => {
    setSettingsOpen(true);
    closeNavigationPanels();
    closePopupSelectorMenus();
  };

  const onOpenSurahSelector = () => {
    setSurahOverlayOpen(true);
    setSurahMenuOpen(false);
    setPageSearchOpen(false);
  };

  const onSubmitTextSearch = async (event) => {
    event?.preventDefault();

    const term = String(searchQuery || "").trim();
    if (!term) {
      setSearchSubmitted(false);
      setSearchResults([]);
      setSearchTotalResults(0);
      setSearchError("");
      return;
    }

    setSearchLoading(true);
    setSearchSubmitted(true);
    setSearchError("");

    try {
      const data = await searchQuran({
        query: term,
        language: "en",
        size: 60,
        page: 1,
        mushaf: 1,
      });

      setSearchResults(Array.isArray(data?.results) ? data.results : []);
      setSearchTotalResults(Number(data?.totalResults) || 0);
    } catch (err) {
      setSearchResults([]);
      setSearchTotalResults(0);
      setSearchError(err?.message || uiLabels.searchError);
    } finally {
      setSearchLoading(false);
    }
  };

  const onSelectSearchResult = async (result) => {
    const pageFromResult = Number(result?.pageNumber);
    const verseKey = String(result?.verseKey || "").trim();

    if (verseKey) {
      setSearchHighlightedVerseKey(verseKey);
    }

    if (Number.isFinite(pageFromResult) && pageFromResult >= 1 && pageFromResult <= MAX_PAGE) {
      setPage(pageFromResult);
    } else if (verseKey) {
      try {
        const resolvedPage = await getVersePageNumber({ verseKey, mushaf: 1 });
        setPage(resolvedPage);
      } catch {
        // Keep current page when page lookup fails.
      }
    }

    closeCommandMenus();
  };

  const onSelectSurah = (chapterId) => {
    const chapter = chapters.find((item) => item.id === Number(chapterId));
    const firstPage = Number(chapter?.pages?.[0]);
    if (Number.isFinite(firstPage) && firstPage >= 1 && firstPage <= MAX_PAGE) {
      setPage(firstPage);
    }
    closeCommandMenus();
  };

  const onLaunchRecitationFromPage = () => {
    const anchor = String(firstVerseKeyOnPage || "").trim();
    startRecitationFromVerse(anchor, "quran");
  };

  const onOpenRecitationReaderPicker = () => {
    setRecitationAnchorVerseKey(firstVerseKeyOnPage || recitationAnchorVerseKey || "");
    setRecitationMode("quran");
    setContentPopup((prev) => ({
      ...prev,
      open: true,
      type: "recitation",
      title: getRecitationTitle("quran"),
      verseKey: firstVerseKeyOnPage || recitationAnchorVerseKey || prev.verseKey,
      loading: false,
      audioUrl: "",
      error: "",
    }));
  };

  const onHeaderPlay = () => {
    if (!recitationSessionActive) {
      return;
    }

    recitationPlaybackStartedRef.current = true;
    if (!tryPlayRecitationAudio()) {
      recitationPendingAutoPlayRef.current = true;
      return;
    }
  };

  const loadTafsirOptions = async (languageCode, preferredResourceId = null) => {
    const lang = String(languageCode || "ar").toLowerCase();
    setTafsirOptionsLoading(true);

    try {
      const resources = await getTafsirResources(lang);
      const nextResourceId =
        preferredResourceId && resources.some((item) => item.id === Number(preferredResourceId))
          ? Number(preferredResourceId)
          : resources[0]?.id ?? null;

      setTafsirLanguageCode(lang);
      setTafsirResources(resources);
      setTafsirResourceId(nextResourceId);
      return { languageCode: lang, resourceId: nextResourceId };
    } finally {
      setTafsirOptionsLoading(false);
    }
  };

  const requestTafsirForVerse = async (verseKey, languageCode, resourceId) => {
    const key = String(verseKey || "").trim();
    if (!key) {
      return;
    }

    const title = getTafsirTitle(languageCode);
    const safeResourceId = Number(resourceId);
    const hasValidResource = Number.isInteger(safeResourceId) && safeResourceId > 0;
    setTafsirBookMenuOpen(false);

    if (!hasValidResource) {
      setContentPopup((prev) => ({
        ...prev,
        open: true,
        type: "tafseer",
        title,
        verseKey: key,
        loading: false,
        text: "",
        audioUrl: "",
        error: `${uiLabels.noTafsirForLanguage} ${String(languageCode || "").toUpperCase()}.`,
        sourceId: null,
        sourceBook: "",
        sourceAuthor: "",
      }));
      return;
    }

    setContentPopup((prev) => ({
      ...prev,
      open: true,
      type: "tafseer",
      title,
      verseKey: key,
      loading: true,
      text: "",
      audioUrl: "",
      error: "",
      sourceId: Number.isFinite(Number(resourceId)) ? Number(resourceId) : null,
      sourceBook: "",
      sourceAuthor: "",
    }));

    try {
      const result = await getVerseTafseer({
        verseKey: key,
        language: languageCode,
        tafseerId: resourceId,
      });

      setContentPopup({
        open: true,
        type: "tafseer",
        title,
        verseKey: result.verseKey,
        loading: false,
        text: result.text,
        audioUrl: "",
        error: "",
        sourceId: result.tafseerId,
        sourceBook: result.resourceName || "",
        sourceAuthor: "",
      });

      if (Number.isFinite(Number(result.tafseerId))) {
        setTafsirResourceId(Number(result.tafseerId));
      }
    } catch (err) {
      setContentPopup((prev) => ({
        ...prev,
        open: true,
        type: "tafseer",
        title,
        verseKey: key,
        loading: false,
        text: "",
        audioUrl: "",
        error: err?.message || uiLabels.tafsirLoadError,
        sourceBook: "",
        sourceAuthor: "",
      }));
    }
  };

  const onChangeTafsirLanguage = async (event) => {
    const nextLanguage = event?.target?.value || event;
    setTafsirLanguageMenuOpen(false);
    setTafsirBookMenuOpen(false);
    const next = await loadTafsirOptions(nextLanguage);

    if (contentPopup.open && contentPopup.type === "tafseer" && contentPopup.verseKey) {
      if (next.resourceId) {
        requestTafsirForVerse(contentPopup.verseKey, next.languageCode, next.resourceId);
      } else {
        setContentPopup((prev) => ({
          ...prev,
          open: true,
          type: "tafseer",
          title: getTafsirTitle(next.languageCode),
          loading: false,
          text: "",
          audioUrl: "",
          error: `${uiLabels.noTafsirForLanguage} ${String(next.languageCode || "").toUpperCase()}.`,
          sourceId: null,
          sourceBook: "",
        }));
      }
    }
  };

  const onChangeTafsirBook = async (event) => {
    const nextResourceId = Number(event?.target?.value || event) || null;
    tafsirBookMenuSuppressUntilRef.current = Date.now() + 350;
    setTafsirResourceId(nextResourceId);
    setTafsirBookMenuOpen(false);

    if (contentPopup.open && contentPopup.type === "tafseer" && contentPopup.verseKey) {
      requestTafsirForVerse(contentPopup.verseKey, tafsirLanguageCode, nextResourceId);
    }
  };

  const onSelectRecitation = () => {
    const selectedVerse = contextMenu.verseKey;
    closeContextMenu();

    if (!selectedVerse) {
      return;
    }

    setRecitationAnchorVerseKey(selectedVerse);
    setRecitationMode("ayah");
    setContentPopup((prev) => ({
      ...prev,
      open: true,
      type: "recitation",
      title: getRecitationTitle("ayah"),
      verseKey: selectedVerse,
      loading: false,
      text: "",
      audioUrl: "",
      error: "",
      sourceId: recitationResourceId,
      sourceBook: "",
      sourceAuthor: "",
    }));
  };

  useEffect(() => {
    setContextMenu({
      open: false,
      x: 0,
      y: 0,
      verseKey: "",
      verticalPlacement: "top",
      horizontalPlacement: "center",
    });
    setQuickPlayPrompt({
      open: false,
      x: 0,
      y: 0,
      verseKey: "",
    });
    setContentPopup({
      open: false,
      type: "translation",
      title: "",
      verseKey: "",
      loading: false,
      text: "",
      audioUrl: "",
      error: "",
      sourceId: null,
      sourceBook: "",
      sourceAuthor: "",
    });
    setBookMenuOpen(false);
    setTranslationLanguageMenuOpen(false);
    setRecitationReaderMenuOpen(false);
    setTafsirLanguageMenuOpen(false);
    setTafsirBookMenuOpen(false);
    setSettingsOpen(false);
  }, [page]);

  useEffect(() => {
    if (
      !bookMenuOpen &&
      !translationLanguageMenuOpen &&
      !recitationReaderMenuOpen &&
      !tafsirLanguageMenuOpen &&
      !tafsirBookMenuOpen
    ) {
      return undefined;
    }

    const onPointerDown = (event) => {
      const insideBook = bookMenuRef.current?.contains(event.target);
      const insideTranslationLanguage = translationLanguageMenuRef.current?.contains(event.target);
      const insideRecitationReader = recitationReaderMenuRef.current?.contains(event.target);
      const insideTafsirLanguage = tafsirLanguageMenuRef.current?.contains(event.target);
      const insideTafsirBook = tafsirBookMenuRef.current?.contains(event.target);

      if (!insideBook) {
        setBookMenuOpen(false);
      }

      if (!insideTranslationLanguage) {
        setTranslationLanguageMenuOpen(false);
      }

      if (!insideRecitationReader) {
        setRecitationReaderMenuOpen(false);
      }

      if (!insideTafsirLanguage) {
        setTafsirLanguageMenuOpen(false);
      }

      if (!insideTafsirBook) {
        setTafsirBookMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [
    bookMenuOpen,
    translationLanguageMenuOpen,
    recitationReaderMenuOpen,
    tafsirLanguageMenuOpen,
    tafsirBookMenuOpen,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadInitialOptions = async () => {
      setTranslationOptionsLoading(true);

      const savedSettings = initialUserSettingsRef.current || {};
      const preferredTranslationLanguage = String(savedSettings.translationLanguageCode || "").toLowerCase();
      const preferredTranslationResourceId = Number(savedSettings.translationResourceId);
      const preferredRecitationResourceId = Number(savedSettings.recitationResourceId);
      const preferredTafsirLanguage = String(savedSettings.tafsirLanguageCode || "").toLowerCase();
      const preferredTafsirResourceId = Number(savedSettings.tafsirResourceId);

      try {
        const langs = await getTranslationLanguages();
        const tafsirLangs = await getTafsirAvailableLanguages();
        if (cancelled) {
          return;
        }

        setTranslationLanguages(langs);
        setTafsirLanguages(tafsirLangs);
        const initialLanguage = langs.some((item) => item.code === preferredTranslationLanguage)
          ? preferredTranslationLanguage
          : langs.some((item) => item.code === "fr")
            ? "fr"
            : langs[0]?.code || "fr";
        await loadTranslationOptions(
          initialLanguage,
          Number.isFinite(preferredTranslationResourceId) && preferredTranslationResourceId > 0
            ? preferredTranslationResourceId
            : null
        );

        await loadRecitationOptions(
          Number.isFinite(preferredRecitationResourceId) && preferredRecitationResourceId > 0
            ? preferredRecitationResourceId
            : null
        );

        const initialTafsirLanguage = tafsirLangs.some((item) => item.code === preferredTafsirLanguage)
          ? preferredTafsirLanguage
          : tafsirLangs.some((item) => item.code === "ar")
            ? "ar"
            : tafsirLangs[0]?.code || "ar";
        await loadTafsirOptions(
          initialTafsirLanguage,
          Number.isFinite(preferredTafsirResourceId) && preferredTafsirResourceId > 0
            ? preferredTafsirResourceId
            : null
        );
      } finally {
        if (!cancelled) {
          setTranslationOptionsLoading(false);
        }
      }
    };

    loadInitialOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const renderPageContent = ({
    presentation,
    pageLines,
    interactive,
    linesClassName = "",
    linesRefProp = undefined,
    showPageNumber = true,
  }) => {
    const showDesktopHeaderNavigation = !isMobileView && interactive;

    return (
      <div
        className={`quran-page-panel ${
          shouldCenterMobilePagePanel(presentation.pageNumber) ? "quran-page-panel--centered" : ""
        }`.trim()}
        dir="rtl"
      >
        <div className="quran-page-panel-body">
          <QuranPageHeader
            showNavigation={showDesktopHeaderNavigation}
            uiLabels={uiLabels}
            canNext={canNext}
            canPrev={canPrev}
            loading={loading}
            onGoNextPage={() => setPage((prev) => Math.min(MAX_PAGE, prev + 1))}
            onGoPrevPage={() => setPage((prev) => Math.max(1, prev - 1))}
            surahLabel={presentation.surahLabel}
            juzLabel={presentation.juzLabel}
          />

          <QuranSurahBanner
            label={presentation.surahBannerLabel}
            ornamentSrc={surahBannerOrnament}
          />

          <div className={`quran-lines ${linesClassName}`.trim()} dir="rtl" ref={linesRefProp}>
            <QuranVerseLines
              lines={pageLines}
              loadedPages={loadedPages}
              loadedFontFamilies={loadedFontFamilies}
              uthmanicFont={UTHMANIC_FONT}
              recitationSessionActive={recitationSessionActive}
              recitationCurrentVerseKey={recitationCurrentVerseKey}
              searchHighlightedVerseKey={searchHighlightedVerseKey}
              interactive={interactive}
              onVersePointerDown={onVersePointerDown}
              onVersePointerUp={onVersePointerUp}
              onVersePointerCancel={onVersePointerCancel}
              showSurahBasmala={presentation.showSurahBasmala}
              fatihaBasmalaLines={fatihaBasmalaLines}
            />
          </div>
        </div>

        {showPageNumber ? (
          <div className="quran-page-panel-footer">
            <div
              className={`quran-bottom-page-number quran-bottom-page-number--panel ${getPageNumberParityClass(
                presentation.pageNumber
              )}`}
            >
              <span>{presentation.pageNumber}</span>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const registerSurahOverlayItemRef = useCallback((chapterId, node) => {
    const safeId = Number(chapterId);
    if (!Number.isFinite(safeId)) {
      return;
    }

    if (!node) {
      surahOverlayItemRefs.current.delete(safeId);
      return;
    }

    surahOverlayItemRefs.current.set(safeId, node);
  }, []);

  return (
    <section
      className={`quran-reading-page quran-reading-page--theme-${uiTheme}`}
      dir="rtl"
      lang="ar"
      style={mobileResponsiveVars}
      onPointerUp={onScreenPointerUp}
      onClick={() => {
        if (contextMenu.open) {
          closeContextMenu();
        }

        if (quickPlayPrompt.open) {
          closeQuickPlayPrompt();
        }
      }}
    >
      <div className="quran-mushaf-stage" ref={mushafStageRef}>
        <div
          className={`quran-mushaf-frame ${useNativePageSwipe ? "quran-mushaf-frame--native-swipe" : ""}`.trim()}
          ref={mushafFrameRef}
          onTouchStartCapture={useNativePageSwipe ? undefined : onSwipeTouchStartCapture}
          onTouchMoveCapture={useNativePageSwipe ? undefined : onSwipeTouchMoveCapture}
          onTouchEndCapture={useNativePageSwipe ? undefined : onSwipeTouchEndCapture}
          onTouchCancelCapture={useNativePageSwipe ? undefined : onSwipeTouchCancelCapture}
          onMouseDownCapture={useNativePageSwipe ? undefined : onSwipeMouseDownCapture}
          onDragStartCapture={(event) => event.preventDefault()}
        >
        <QuranCommandsTop
          isVisible={commandMenusVisible}
          uiLabels={uiLabels}
          surahMenuOpen={surahMenuOpen}
          chapters={chapters}
          onOpenSettings={onOpenSettingsPanel}
          onOpenSearch={onOpenTextSearch}
          onOpenSurahSelector={onOpenSurahSelector}
          onSelectSurah={onSelectSurah}
        />
        {showInitialLoading && <div className="quran-state">{uiLabels.loadingPage}</div>}
        {!hasRenderablePage && !loading && error && <div className="quran-state quran-state-error">{error}</div>}

        {hasRenderablePage && (
          <>
            {!useNativePageSwipe && (
              <>
                <QuranPageHeader
                  showNavigation={true}
                  uiLabels={uiLabels}
                  canNext={canNext}
                  canPrev={canPrev}
                  loading={loading}
                  onGoNextPage={() => setPage((prev) => Math.min(MAX_PAGE, prev + 1))}
                  onGoPrevPage={() => setPage((prev) => Math.max(1, prev - 1))}
                  surahLabel={surahLabel}
                  juzLabel={juzLabel}
                />

                <QuranSurahBanner
                  label={surahBannerLabel}
                  ornamentSrc={surahBannerOrnament}
                />

              </>
            )}

            <QuranPageViewport
              swipeViewportRef={swipeViewportRef}
              useNativePageSwipe={useNativePageSwipe}
              nativeSwipeScrollerRef={nativeSwipeScrollerRef}
              onNativeSwipeScroll={onNativeSwipeScroll}
              onNativeSwipeTouchStart={onNativeSwipeTouchStart}
              onNativeSwipeTouchMove={onNativeSwipeTouchMove}
              onNativeSwipeTouchEnd={onNativeSwipeTouchEnd}
              onNativeSwipeMouseDown={onNativeSwipeMouseDown}
              onNativeSwipeMouseMove={onNativeSwipeMouseMove}
              onNativeSwipeMouseUp={onNativeSwipeMouseUp}
              canNext={canNext}
              canPrev={canPrev}
              renderPageContent={renderPageContent}
              nextPagePresentation={nextPagePresentation}
              nextPageLines={nextPageLines}
              currentPagePresentation={currentPagePresentation}
              lines={lines}
              linesRef={linesRef}
              prevPagePresentation={prevPagePresentation}
              prevPageLines={prevPageLines}
              swipePreview={swipePreview}
              swipePreviewTargetReady={swipePreviewTargetReady}
              swipeTargetLines={swipeTargetLines}
              loadedPages={loadedPages}
              loadedFontFamilies={loadedFontFamilies}
              uthmanicFont={UTHMANIC_FONT}
              recitationSessionActive={recitationSessionActive}
              recitationCurrentVerseKey={recitationCurrentVerseKey}
              searchHighlightedVerseKey={searchHighlightedVerseKey}
              onVersePointerDown={onVersePointerDown}
              onVersePointerUp={onVersePointerUp}
              onVersePointerCancel={onVersePointerCancel}
              showSurahBasmala={showSurahBasmala}
              fatihaBasmalaLines={fatihaBasmalaLines}
              loading={loading}
              pageTransitionClass={pageTransitionClass}
              page={page}
              swipePreviewGapPx={SWIPE_PREVIEW_GAP_PX}
            />

            {!useNativePageSwipe && (
              <div className={`quran-bottom-page-number ${getPageNumberParityClass(page)}`}>
                <span>{page}</span>
              </div>
            )}

            {loading && !useNativePageSwipe && <div className="quran-page-loading-indicator">{uiLabels.loading}</div>}
          </>
        )}
        </div>
      </div>

      <QuranRecitationControls
        isVisible={commandMenusVisible && !recitationAudioDockVisible}
        uiLabels={uiLabels}
        onLaunchRecitation={onLaunchRecitationFromPage}
        firstVerseKeyOnPage={firstVerseKeyOnPage}
        onOpenReaderPicker={onOpenRecitationReaderPicker}
        readerLabel={
          recitationCurrentReaderLabel ||
          recitationResources.find((item) => item.id === recitationResourceId)?.displayName ||
          uiLabels.recitation
        }
      />

      <QuranCommandsScroller
        isVisible={commandMenusVisible}
        uiLabels={uiLabels}
        canNext={canNext}
        canPrev={canPrev}
        page={page}
        maxPage={MAX_PAGE}
        onGoNextPage={() => setPage((prev) => Math.min(MAX_PAGE, prev + 1))}
        onGoPrevPage={() => setPage((prev) => Math.max(1, prev - 1))}
        onChangePage={(nextPage) => setPage(nextPage)}
      />

      {(recitationSessionActive || recitationSessionLoading) && recitationAudioDockVisible && (
        <QuranAudioDock
          commandsVisible={commandsVisible}
          uiLabels={uiLabels}
          recitationCurrentReaderLabel={recitationCurrentReaderLabel}
          recitationCurrentChapterLabel={recitationCurrentChapterLabel}
          recitationCurrentVerseKey={recitationCurrentVerseKey}
          onHide={() => {
            setRecitationAudioDockVisible(false);
            setAudioDockHiddenByScreenTap(false);
          }}
          onStop={stopRecitationSession}
          recitationCurrentTime={recitationCurrentTime}
          recitationProgressPercent={recitationProgressPercent}
          onSeek={onRecitationSeek}
          recitationDuration={recitationDuration}
          canRecitationPrev={canRecitationPrev}
          onPrevious={onRecitationPrevious}
          recitationIsPlaying={recitationIsPlaying}
          onTogglePlayPause={onTogglePlayPause}
          recitationSessionActive={recitationSessionActive}
          recitationCurrentAudioUrl={recitationCurrentAudioUrl}
          canRecitationNext={canRecitationNext}
          onNext={onRecitationNext}
          onToggleRate={onToggleRecitationRate}
          recitationRate={recitationRate}
          formatTime={formatTime}
        />
      )}

      {(recitationSessionActive || recitationSessionLoading) && (
        <audio
          ref={recitationAudioRef}
          className="quran-head-recitation-audio"
          src={recitationCurrentAudioUrl}
          preload="auto"
          onEnded={onRecitationEnded}
          onPlay={onRecitationPlay}
          onPause={onRecitationPause}
          onLoadedData={onRecitationLoadedData}
          onLoadedMetadata={onRecitationLoadedMetadata}
          onTimeUpdate={onRecitationTimeUpdate}
        />
      )}

      <QuranSurahOverlay
        isOpen={surahOverlayOpen}
        usePopupLayout={!isMobileView}
        uiLanguage={uiLanguage}
        uiLabels={uiLabels}
        chapters={chapters}
        currentChapterIdOnPage={currentChapterIdOnPage}
        onClose={() => setSurahOverlayOpen(false)}
        onSelectSurah={onSelectSurah}
        registerItemRef={registerSurahOverlayItemRef}
      />

      <QuranSettingsOverlay
        isOpen={settingsOpen}
        uiLanguage={uiLanguage}
        uiLabels={uiLabels}
        uiTheme={uiTheme}
        verseFontScale={verseFontScale}
        onClose={() => setSettingsOpen(false)}
        onChangeTheme={setUiTheme}
        onChangeLanguage={setUiLanguage}
        onChangeVerseFontScale={setVerseFontScale}
      />

      <QuranSearchOverlay
        isOpen={pageSearchOpen}
        usePopupLayout={!isMobileView}
        uiLanguage={uiLanguage}
        uiLabels={uiLabels}
        searchInputRef={searchInputRef}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        onClose={() => setPageSearchOpen(false)}
        onSubmit={onSubmitTextSearch}
        searchSubmitted={searchSubmitted}
        searchLoading={searchLoading}
        searchError={searchError}
        searchTotalResults={searchTotalResults}
        searchResults={searchResults}
        onSelectResult={onSelectSearchResult}
      />

      <QuranContextMenu
        isOpen={contextMenu.open}
        contextMenu={contextMenu}
        uiLabels={uiLabels}
        onSelectTranslation={onSelectFrenchTranslation}
        onSelectRecitation={onSelectRecitation}
        onClose={closeContextMenu}
      />

      <QuranQuickPlayButton
        isOpen={quickPlayPrompt.open}
        quickPlayPrompt={quickPlayPrompt}
        uiLabels={uiLabels}
        onQuickPlay={onQuickPlayRecitation}
      />

      <QuranContentPopup
        isOpen={contentPopup.open}
        uiLanguage={uiLanguage}
        contentPopup={contentPopup}
        closeContentPopup={closeContentPopup}
        uiLabels={uiLabels}
        translationPopupDirection={translationPopupDirection}
        tafsirPopupDirection={tafsirPopupDirection}
        translationVerseWords={translationVerseWords}
        loadedPages={loadedPages}
        loadedFontFamilies={loadedFontFamilies}
        uthmanicFont={UTHMANIC_FONT}
        truncateLabel={truncateLabel}
        translationLanguageMenuRef={translationLanguageMenuRef}
        translationOptionsLoading={translationOptionsLoading}
        translationLanguageMenuOpen={translationLanguageMenuOpen}
        setTranslationLanguageMenuOpen={setTranslationLanguageMenuOpen}
        translationLanguageOptions={translationLanguageOptions}
        translationLanguageCode={translationLanguageCode}
        onSelectTranslationLanguage={onSelectTranslationLanguage}
        bookMenuRef={bookMenuRef}
        bookMenuSuppressUntilRef={bookMenuSuppressUntilRef}
        translationResources={translationResources}
        bookMenuOpen={bookMenuOpen}
        setBookMenuOpen={setBookMenuOpen}
        translationResourceId={translationResourceId}
        onSelectTranslationBook={onSelectTranslationBook}
        tafsirLanguageMenuRef={tafsirLanguageMenuRef}
        tafsirOptionsLoading={tafsirOptionsLoading}
        tafsirLanguageMenuOpen={tafsirLanguageMenuOpen}
        setTafsirLanguageMenuOpen={setTafsirLanguageMenuOpen}
        tafsirLanguageOptions={tafsirLanguageOptions}
        tafsirLanguageCode={tafsirLanguageCode}
        onChangeTafsirLanguage={onChangeTafsirLanguage}
        tafsirBookMenuRef={tafsirBookMenuRef}
        tafsirBookMenuSuppressUntilRef={tafsirBookMenuSuppressUntilRef}
        tafsirResources={tafsirResources}
        tafsirBookMenuOpen={tafsirBookMenuOpen}
        setTafsirBookMenuOpen={setTafsirBookMenuOpen}
        tafsirResourceId={tafsirResourceId}
        onChangeTafsirBook={onChangeTafsirBook}
        recitationMode={recitationMode}
        onChangeRecitationMode={onChangeRecitationMode}
        recitationReaderMenuRef={recitationReaderMenuRef}
        recitationReaderMenuSuppressUntilRef={recitationReaderMenuSuppressUntilRef}
        recitationOptionsLoading={recitationOptionsLoading}
        recitationResources={recitationResources}
        recitationReaderMenuOpen={recitationReaderMenuOpen}
        setRecitationReaderMenuOpen={setRecitationReaderMenuOpen}
        recitationResourceId={recitationResourceId}
        onChangeRecitationReader={onChangeRecitationReader}
        onStartRecitationFromPopup={onStartRecitationFromPopup}
      />
    </section>
  );
};

export default QuranPage;


